"use client"

// src/accordion/index.tsx
import * as RadixAccordion from "@radix-ui/react-accordion";
import { RiArrowDownSLine } from "@remixicon/react";
import React, { useState } from "react";
import { keyframes, styled } from "styled-components";

// src/theme/colors.ts
import { memo } from "@lowerdeck/memo";

// src/theme/tokens.ts
var toDashCase = (str) => {
  return str.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`).replaceAll("_", "-");
};
var createThemeToken = (defaultValue) => (name) => {
  let id = toDashCase(name);
  let value = `var(--${id}${defaultValue ? `, ${defaultValue}` : ""})`;
  return Object.assign(value, {
    id,
    name,
    value,
    defaultValue,
    varName: `--${id}`
  });
};
var recursivelyCallThemeTokenFactory = (tokens) => {
  let result = {};
  for (let key in tokens) {
    let token = tokens[key];
    if (typeof token === "function") {
      result[key] = token(key);
    } else {
      result[key] = recursivelyCallThemeTokenFactory(token);
    }
  }
  return result;
};
var createTheme = (tokens) => {
  let values = recursivelyCallThemeTokenFactory(tokens);
  let setBodyStyles = (theme2) => {
    for (let value of Object.values(theme2)) {
      if ("value" in value) {
        if (value.defaultValue) {
          document.body.style.setProperty(value.varName, value.defaultValue);
        }
      } else if (typeof value === "object") {
        setBodyStyles(value);
      }
    }
  };
  return {
    ...values,
    setRootStyles: (theme2) => {
      let styles = {};
      let recurse = (theme3, values2) => {
        for (let key in theme3) {
          let value = theme3[key];
          if (typeof value == "string" || value.value) {
            let cssKey = values2[key]?.varName;
            if (value.value) value = value.value;
            if (value && cssKey) {
              styles[cssKey] = value;
            }
          } else {
            recurse(theme3[key], values2[key]);
          }
        }
      };
      recurse(theme2, values);
      return styles;
    },
    setBodyStyles: () => setBodyStyles(values)
  };
};

// src/theme/colors.ts
var colors = {
  gray100: "#fafafa",
  gray200: "#f6f6f6",
  gray300: "#efefef",
  gray400: "#ddd",
  gray500: "#ccc",
  gray600: "#999",
  gray700: "#555",
  gray800: "#333",
  gray900: "#222",
  blue100: "#fbfdff",
  blue200: "#f4faff",
  blue300: "#e6f4fe",
  blue400: "#d5efff",
  blue500: "#c2e5ff",
  blue600: "#acd8fc",
  blue700: "#8ec8f6",
  blue800: "#5eb1ef",
  blue900: "#0090ff",
  pink100: "#fffcfd",
  pink200: "#fef7f9",
  pink300: "#ffe9f0",
  pink400: "#fedce7",
  pink500: "#facedd",
  pink600: "#f3bed1",
  pink700: "#eaacc3",
  pink800: "#e093b2",
  pink900: "#e93d82",
  cyan100: "#fafdfe",
  cyan200: "#f2fafb",
  cyan300: "#def7f9",
  cyan400: "#caf1f6",
  cyan500: "#b5e9f0",
  cyan600: "#9ddde7",
  cyan700: "#7dcedc",
  cyan800: "#3db9cf",
  cyan900: "#00a2c7",
  indigo100: "#fdfdfe",
  indigo200: "#f7f9ff",
  indigo300: "#edf2fe",
  indigo400: "#e1e9ff",
  indigo500: "#d2deff",
  indigo600: "#c1d0ff",
  indigo700: "#abbdf9",
  indigo800: "#8da4ef",
  indigo900: "#3e63dd",
  iris100: "#fdfdff",
  iris200: "#f8f8ff",
  iris300: "#f0f1fe",
  iris400: "#e6e7ff",
  iris500: "#dadcff",
  iris600: "#cbcdff",
  iris700: "#b8baf8",
  iris800: "#9b9ef0",
  iris900: "#5b5bd6",
  green100: "#fbfefd",
  green200: "#f4fbf7",
  green300: "#e6f7ed",
  green400: "#d6f1e3",
  green500: "#c3e9d7",
  green600: "#acdec8",
  green700: "#8bceb6",
  green800: "#56ba9f",
  green900: "#29a383",
  orange100: "#fefcfb",
  orange200: "#fff7ed",
  orange300: "#ffefd6",
  orange400: "#ffdfb5",
  orange500: "#ffd19a",
  orange600: "#ffc182",
  orange700: "#f5ae73",
  orange800: "#ec9455",
  orange900: "#f76b15",
  purple100: "#fefcfe",
  purple200: "#fbf7fe",
  purple300: "#f7edfe",
  purple400: "#f2e2fc",
  purple500: "#ead5f9",
  purple600: "#e0c4f4",
  purple700: "#d1afec",
  purple800: "#be93e4",
  purple900: "#8e4ec6",
  red100: "#fffcfc",
  red200: "#fff7f7",
  red300: "#feebec",
  red400: "#ffdbdc",
  red500: "#ffcdce",
  red600: "#e5484d",
  red700: "#e5484d",
  red800: "#e5484d",
  red900: "#e5484d",
  violet100: "#fdfcfe",
  violet200: "#faf8ff",
  violet300: "#f4f0fe",
  violet400: "#ebe4ff",
  violet500: "#e1d9ff",
  violet600: "#d4cafe",
  violet700: "#c2b5f5",
  violet800: "#aa99ec",
  violet900: "#6e56cf",
  yellow100: "#fefdfb",
  yellow200: "#fefbe9",
  yellow300: "#fff7c2",
  yellow400: "#ffee9c",
  yellow500: "#fbe577",
  yellow600: "#f3d673",
  yellow700: "#e9c162",
  yellow800: "#e2a336",
  yellow900: "#ffc53d",
  white100: "#fff",
  white200: "#fff",
  white300: "#fff",
  white400: "#fff",
  white500: "#fff",
  white600: "#fff",
  white700: "#fff",
  white800: "#fff",
  white900: "#fff",
  black100: "#000",
  black200: "#000",
  black300: "#000",
  black400: "#000",
  black500: "#000",
  black600: "#000",
  black700: "#000",
  black800: "#000",
  black900: "#000",
  primary100: "#b1e0ff",
  primary200: "#76c8ff",
  primary300: "#4eb8ff",
  primary400: "#27a9ff",
  primary500: "#0099ff",
  primary600: "#0081d8",
  primary700: "#006ab1",
  primary800: "#005289",
  primary900: "#003b62",
  primary: "#0099ff"
};
var colorThemeVars = Object.fromEntries(Object.entries(colors).map(([key, value]) => [key, createThemeToken(value)]));
var getColorKey = (color, facet) => `${color}${facet}`;
var getForegroundColor = memo((color) => {
  if (color.startsWith("white") || color.startsWith("yellow")) {
    return "#000";
  }
  let value = colors[color];
  let r = parseInt(value.substr(1, 2), 16);
  let g = parseInt(value.substr(3, 2), 16);
  let b = parseInt(value.substr(5, 2), 16);
  let brightness = (r * 299 + g * 587 + b * 114) / 1e3;
  return brightness > 125 ? "#000" : "#fff";
});

// src/theme/index.ts
var theme = createTheme({
  colors: {
    foreground: createThemeToken("#111111"),
    background: createThemeToken("#ffffff"),
    ...colorThemeVars
  },
  shadows: {
    small: createThemeToken("0 2px 4px rgba(0, 0, 0, 0.1)"),
    medium: createThemeToken("0 4px 8px rgba(0, 0, 0, 0.1)"),
    large: createThemeToken("0 8px 16px rgba(0, 0, 0, 0.1)")
  }
});

// src/accordion/index.tsx
var slideDown = keyframes`
  from { height: 0 }
  to { height: var(--radix-accordion-content-height) }
`;
var slideUp = keyframes`
  from { height: var(--radix-accordion-content-height) }
  to { height: 0 }
`;
var Item2 = styled(RadixAccordion.Item).withConfig({
  componentId: "sc-jn5ooz-0"
})`border:1px solid ${theme.colors.gray300};display:flex;flex-direction:column;background:${theme.colors.background};overflow:hidden;border-radius:10px;transition:border-color 0.2s ease;&:focus-within{border-color:${theme.colors.gray500};}&:not(:first-child){margin-top:10px;}`;
var Trigger2 = styled(RadixAccordion.Trigger).attrs({
  type: "button"
}).withConfig({
  componentId: "sc-jn5ooz-1"
})`min-height:48px;display:flex;align-items:center;justify-content:space-between;width:100%;padding:12px 16px;font-size:14px;font-weight:600;border:none;outline:none;cursor:pointer;background:transparent;text-align:left;user-select:none;color:${theme.colors.gray900};&[data-disabled]{color:${theme.colors.gray600};cursor:not-allowed;}`;
var TriggerTitleText = styled.span.withConfig({
  componentId: "sc-jn5ooz-2"
})`display:inline-flex;flex-direction:column;gap:3px;`;
var TriggerDescription = styled.span.withConfig({
  componentId: "sc-jn5ooz-3"
})`font-size:12px;font-weight:500;color:${theme.colors.gray600};line-height:1.2;`;
var Content2 = styled(RadixAccordion.Content).withConfig({
  componentId: "sc-jn5ooz-4"
})`font-size:14px;border-top:1px solid ${theme.colors.gray300};overflow:hidden;background:${theme.colors.background};&[data-state='open']{animation:${slideDown} 0.3s ease;height:auto;}&[data-state='closed']{animation:${slideUp} 0.3s ease forwards;}`;
var Accordion = (p) => {
  let [internalValue, setInternalValue] = useState(() => {
    if (p.type === "multiple") {
      return p.items.flatMap((item, i) => item.defaultOpen ? [i.toString()] : []);
    }
    let initialIndex = p.items.findIndex((item) => item.defaultOpen);
    return initialIndex >= 0 ? initialIndex.toString() : void 0;
  });
  let isControlled = p.value !== void 0;
  let resolvedValue = isControlled ? p.value : internalValue;
  let handleValueChange = (nextValue) => {
    if (!isControlled) {
      if (Array.isArray(nextValue)) {
        setInternalValue(nextValue);
      } else {
        setInternalValue(nextValue || void 0);
      }
    }
    p.onValueChange?.(nextValue);
  };
  return /* @__PURE__ */ React.createElement(RadixAccordion.Root, { type: p.type ?? "single", collapsible: (p.type ?? "single") === "single" ? p.collapsible : void 0, disabled: p.disabled, value: resolvedValue, onValueChange: handleValueChange }, p.items.map((item, i) => {
    let isOpen = Array.isArray(resolvedValue) ? resolvedValue.includes(i.toString()) : resolvedValue == i.toString();
    return /* @__PURE__ */ React.createElement(Item2, { value: i.toString(), key: i, disabled: item.disabled }, /* @__PURE__ */ React.createElement(Trigger2, { disabled: item.disabled }, /* @__PURE__ */ React.createElement(TriggerTitleText, null, /* @__PURE__ */ React.createElement("span", null, item.title), item.description ? /* @__PURE__ */ React.createElement(TriggerDescription, null, item.description) : null), /* @__PURE__ */ React.createElement(RiArrowDownSLine, { style: {
      transform: `rotate(${isOpen ? 180 : 0}deg)`,
      transition: "transform 200ms ease",
      opacity: 0.5
    }, size: 16 })), /* @__PURE__ */ React.createElement(Content2, null, /* @__PURE__ */ React.createElement("div", { style: {
      padding: "16px"
    } }, item.content)));
  }));
};
var AccordionSingle = ({
  title,
  children,
  disabled,
  defaultOpen
}) => {
  return /* @__PURE__ */ React.createElement(Accordion, { items: [{
    title,
    disabled,
    content: children,
    defaultOpen
  }], type: "multiple" });
};

// src/alert/index.tsx
import * as RadixAlertDialog from "@radix-ui/react-alert-dialog";
import React3 from "react";
import { keyframes as keyframes2, styled as styled2 } from "styled-components";

// src/dialog/state.ts
import React2, { useContext, useEffect, useState as useState2 } from "react";
var useDialog = (initialState = false) => {
  let [isOpen, setIsOpen] = useState2(initialState);
  return {
    state: {
      isOpen,
      onOpenChange: setIsOpen
    },
    isOpen,
    open: () => setIsOpen(true)
  };
};
var currentZIndexRef = {
  value: 1e3
};
var useDialogZIndex = (isOpen) => {
  let [zIndex, setZIndex] = useState2(() => currentZIndexRef.value + 5);
  useEffect(() => {
    currentZIndexRef.value += 10;
    setZIndex(currentZIndexRef.value);
  }, [isOpen]);
  return zIndex;
};
var DialogContext = React2.createContext(null);
var DialogProvider = DialogContext.Provider;
var useDialogContext = () => useContext(DialogContext);
var useIsInDialog = () => !!useContext(DialogContext);

// src/alert/index.tsx
var fadeInShift = keyframes2`
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;
var fadeOutShift = keyframes2`
  from {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
  to {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }
`;
var fadeIn = keyframes2`
  from {
    opacity: 0;
  }
  to {
    opacity: 0.4;
  }
`;
var fadeOut = keyframes2`
  from {
    opacity: 0.4;
  }
  to {
    opacity: 0;
  }
`;
var Overlay2 = styled2(RadixAlertDialog.Overlay).withConfig({
  componentId: "sc-1ag8o3v-0"
})`position:fixed;top:0;left:0;right:0;bottom:0;background:${theme.colors.gray600};z-index:1000;&[data-state='open']{animation:${fadeIn} 200ms ease-out forwards;}&[data-state='closed']{animation:${fadeOut} 200ms ease-in forwards;}`;
var Content4 = styled2(RadixAlertDialog.Content).withConfig({
  componentId: "sc-1ag8o3v-1"
})`position:fixed;z-index:1001;top:50%;left:50%;transform:translate(-50%,-50%);width:90vw;max-width:400px;max-height:90vh;overflow:auto;background:${theme.colors.gray200};border-radius:10px;box-shadow:0 0 10px rgba(0,0,0,0.1);color:${theme.colors.gray900};&[data-state='open']{animation:${fadeInShift} 200ms ease-out;}&[data-state='closed']{animation:${fadeOutShift} 200ms ease-in;}`;
var Title2 = styled2(RadixAlertDialog.Title).withConfig({
  componentId: "sc-1ag8o3v-2"
})`padding:20px 20px 0px 20px;font-size:20px;font-weight:600;`;
var Description2 = styled2(RadixAlertDialog.Description).withConfig({
  componentId: "sc-1ag8o3v-3"
})`padding:6px 20px 0px 20px;font-size:14px;color:${theme.colors.gray600};`;
var Actions = styled2("div").withConfig({
  componentId: "sc-1ag8o3v-4"
})`display:flex;justify-content:flex-end;gap:10px;padding:20px;`;
var Alert = {
  Wrapper: ({
    children,
    isOpen,
    onOpenChange
  }) => {
    let zIndex = useDialogZIndex(isOpen);
    return /* @__PURE__ */ React3.createElement(RadixAlertDialog.Root, { open: isOpen, onOpenChange }, /* @__PURE__ */ React3.createElement(RadixAlertDialog.Portal, null, /* @__PURE__ */ React3.createElement(Overlay2, { style: {
      zIndex
    } }), /* @__PURE__ */ React3.createElement(Content4, { style: {
      zIndex: zIndex + 1
    } }, children)));
  },
  Title: ({
    children
  }) => {
    return /* @__PURE__ */ React3.createElement(Title2, null, children);
  },
  Description: ({
    children
  }) => {
    return /* @__PURE__ */ React3.createElement(Description2, null, children);
  },
  Actions: ({
    children
  }) => {
    return /* @__PURE__ */ React3.createElement(Actions, null, children);
  },
  Action: ({
    children,
    type
  }) => {
    if (type == "cancel") {
      return /* @__PURE__ */ React3.createElement(RadixAlertDialog.Cancel, { asChild: true }, children);
    }
    return /* @__PURE__ */ React3.createElement(RadixAlertDialog.Action, { asChild: true }, children);
  }
};

// src/animateContent/index.tsx
import { motion } from "framer-motion";
import React4, { useEffect as useEffect2, useMemo, useRef, useState as useState3 } from "react";
var AnimateContent = (props) => {
  let [prevIdentifier, setPrevIdentifier] = useState3(() => props.identifier);
  let direction = useMemo(() => {
    if (props.identifier != prevIdentifier) return "animate";
    return "none";
  }, [props.identifier, prevIdentifier]);
  let prevChildren = useRef(props.children);
  useEffect2(() => {
    let to = setTimeout(() => {
      setPrevIdentifier(props.identifier);
      prevChildren.current = props.children;
    }, 300);
    return () => clearTimeout(to);
  }, [props.identifier]);
  useEffect2(() => {
    if (direction == "none") prevChildren.current = props.children;
  }, [props.children, direction]);
  return /* @__PURE__ */ React4.createElement("div", { style: {
    // display: 'flex',
    width: "100%",
    position: "relative",
    padding: `0px ${props.paddingInline ?? 20}px`
  } }, /* @__PURE__ */ React4.createElement(motion.div, { initial: direction == "animate" ? {
    opacity: 0,
    scale: 1.2
  } : {
    opacity: 1
  }, animate: {
    opacity: 1,
    scale: 1
  }, transition: {
    duration: 0.2,
    ease: "easeInOut"
  }, key: props.identifier }, props.children), direction != "none" && /* @__PURE__ */ React4.createElement(motion.div, { initial: {
    opacity: 1,
    scale: 1
  }, animate: {
    opacity: 0,
    scale: 0.8
  }, transition: {
    duration: 0.2,
    ease: "easeInOut"
  }, key: prevIdentifier, style: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%"
  } }, prevChildren.current));
};

// src/animateHeight/index.tsx
import { motion as motion2 } from "framer-motion";
import React5 from "react";
import { useMeasure } from "react-use";
var isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
var AnimateHeight = (props) => {
  let [ref, {
    height
  }] = useMeasure();
  if (isSafari) {
    return /* @__PURE__ */ React5.createElement("div", null, props.children);
  }
  return /* @__PURE__ */ React5.createElement(motion2.div, { animate: {
    height
  }, transition: {
    duration: 0.2
  }, style: {
    width: "100%",
    overflowY: "hidden"
  } }, /* @__PURE__ */ React5.createElement("div", { ref }, props.children));
};

// src/animatePanes/index.tsx
import { motion as motion3 } from "framer-motion";
import React6, { useEffect as useEffect3, useMemo as useMemo2, useRef as useRef2, useState as useState4 } from "react";
var AnimatePanes = (props) => {
  let [prevOrderedIdentifier, setPrevOrderedIdentifier] = useState4(() => props.orderedIdentifier);
  let direction = useMemo2(() => {
    if (props.orderedIdentifier > prevOrderedIdentifier) return "left";
    if (props.orderedIdentifier < prevOrderedIdentifier) return "right";
    return "none";
  }, [props.orderedIdentifier, prevOrderedIdentifier]);
  let prevChildren = useRef2(props.children);
  useEffect3(() => {
    let to = setTimeout(() => {
      setPrevOrderedIdentifier(props.orderedIdentifier);
      prevChildren.current = props.children;
    }, 300 + (props.delayMs ?? 0));
    return () => clearTimeout(to);
  }, [props.orderedIdentifier, props.delayMs, props.children]);
  useEffect3(() => {
    if (direction == "none") prevChildren.current = props.children;
  }, [props.children, direction]);
  return /* @__PURE__ */ React6.createElement(AnimateHeight, null, /* @__PURE__ */ React6.createElement("div", { style: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    overflow: "hidden",
    position: "relative",
    padding: 3
  } }, /* @__PURE__ */ React6.createElement(motion3.div, { initial: {
    x: direction == "right" ? "-100%" : direction == "left" ? "100%" : 0
  }, animate: {
    x: 0
  }, transition: {
    duration: 0.2,
    ease: "easeInOut",
    delay: (props.delayMs ?? 0) / 1e3
  }, key: props.orderedIdentifier }, props.children), direction != "none" && /* @__PURE__ */ React6.createElement(motion3.div, { initial: {
    x: 0
  }, animate: {
    x: direction == "right" ? "100%" : "-100%"
  }, transition: {
    duration: 0.2,
    ease: "easeInOut",
    delay: (props.delayMs ?? 0) / 1e3
  }, key: prevOrderedIdentifier, style: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%"
  } }, prevChildren.current)));
};

// src/atoms/index.ts
import { useLayoutEffect, useState as useState5 } from "react";

// src/atoms/listeners.ts
var createListeners = () => {
  let listeners = /* @__PURE__ */ new Set();
  let register = (listener) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };
  let notify = () => {
    for (let listener of listeners) listener();
  };
  let dispose = () => {
    listeners.clear();
  };
  return {
    register,
    notify,
    dispose
  };
};

// src/atoms/index.ts
var atom = (initialValue) => {
  let value = initialValue;
  let listeners = createListeners();
  let set = (newValue) => {
    if (typeof newValue === "function") {
      value = newValue(value);
    } else {
      value = newValue;
    }
    listeners.notify();
  };
  let get = () => value;
  let subscribe = (listener) => {
    let cb = () => listener(get());
    return listeners.register(cb);
  };
  return {
    set,
    get,
    subscribe
  };
};
var useAtom = (a) => {
  let [_, setState] = useState5({});
  useLayoutEffect(() => a.subscribe(() => setState({})), [a]);
  return a.get();
};

// src/attributes/index.tsx
import React9 from "react";
import { styled as styled5 } from "styled-components";

// src/text/code.tsx
import React7 from "react";
import { styled as styled3 } from "styled-components";

// src/text/constants.ts
var textSizeSteps = {
  "1": {
    "--font-size": "12px",
    "--letter-spacing": "0.0025em",
    "--line-height": "16px",
    "--mobile-scale": "1"
  },
  "2": {
    "--font-size": "14px",
    "--letter-spacing": "0em",
    "--line-height": "20px",
    "--mobile-scale": "1"
  },
  "3": {
    "--font-size": "16px",
    "--letter-spacing": "0em",
    "--line-height": "24px",
    "--mobile-scale": "1"
  },
  "4": {
    "--font-size": "18px",
    "--letter-spacing": "-0.0025em",
    "--line-height": "26px",
    "--mobile-scale": "1"
  },
  "5": {
    "--font-size": "20px",
    "--letter-spacing": "-0.005em",
    "--line-height": "28px",
    "--mobile-scale": "0.95"
  },
  "6": {
    "--font-size": "24px",
    "--letter-spacing": "-0.00625em",
    "--line-height": "30px",
    "--mobile-scale": "0.95"
  },
  "7": {
    "--font-size": "28px",
    "--letter-spacing": "-0.0075em",
    "--line-height": "36px",
    "--mobile-scale": "0.9"
  },
  "8": {
    "--font-size": "35px",
    "--letter-spacing": "-0.01em",
    "--line-height": "40px",
    "--mobile-scale": "0.8"
  },
  "9": {
    "--font-size": "60px",
    "--letter-spacing": "-0.025em",
    "--line-height": "60px",
    "--mobile-scale": "0.7"
  }
};
var textWeights = {
  light: {
    fontWeight: 300
  },
  regular: {
    fontWeight: 400
  },
  medium: {
    fontWeight: 500
  },
  strong: {
    fontWeight: 600
  },
  bold: {
    fontWeight: 700
  }
};
var textAlignments = {
  left: {
    textAlign: "left"
  },
  center: {
    textAlign: "center"
  },
  right: {
    textAlign: "right"
  }
};
var textWrap = {
  wrap: {
    whiteSpace: "normal"
  },
  nowrap: {
    whiteSpace: "nowrap"
  },
  balance: {
    textWrap: "balance"
  }
};
var textTransforms = {
  uppercase: {
    textTransform: "uppercase"
  },
  lowercase: {
    textTransform: "lowercase"
  },
  capitalize: {
    textTransform: "capitalize"
  }
};
var truncateStyles = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};
var getTextStyles = (props) => ({
  margin: 0,
  padding: 0,
  fontFamily: "inherit",
  ...textSizeSteps[props.size || "2"],
  ...textWeights[props.weight || "regular"],
  ...textAlignments[props.align || "left"],
  ...textWrap[props.wrap || "wrap"],
  ...props.transform && textTransforms[props.transform],
  ...props.truncate && truncateStyles,
  color: props.color ? theme.colors[props.color] : theme.colors.foreground
});

// src/text/code.tsx
var StyledCode = styled3("code").withConfig({
  componentId: "sc-18y1v5y-0"
})`font-family:'JetBrains Mono','SFMono-Regular',Consolas,'Liberation Mono',Menlo,Courier,monospace;background:transparent;padding:0.2em 0.4em;color:${theme.colors.gray900};`;
var Code = (props) => {
  let size = getTextStyles(props);
  return /* @__PURE__ */ React7.createElement(StyledCode, { style: {
    ...size,
    ...props.style
  } }, props.children);
};
var StyledKbd = styled3("kbd").withConfig({
  componentId: "sc-18y1v5y-1"
})`background-color:${theme.colors.gray100};box-shadow:inset 0 -1px 0 ${theme.colors.gray300};border-radius:0.2em;padding:0.1em 0.3em;font-size:0.9em;font-family:'JetBrains Mono','SFMono-Regular',Consolas,'Liberation Mono',Menlo,Courier,monospace;`;
var Kbd = (props) => {
  return /* @__PURE__ */ React7.createElement(StyledKbd, { style: {
    ...props.style
  } }, props.children);
};

// src/text/text.tsx
import React8 from "react";
import Balancer from "react-wrap-balancer";
import { styled as styled4 } from "styled-components";
var Element = styled4("div").withConfig({
  componentId: "sc-1ee7qw3-0"
})`font-size:var(--font-size);letter-spacing:var(--letter-spacing);line-height:var(--line-height);@media (max-width:768px){font-size:calc(var(--font-size) * var(--mobile-scale));line-height:calc(var(--line-height) * var(--mobile-scale));}`;
var Text = (props) => {
  let styles = getTextStyles(props);
  return React8.createElement(Element, {
    style: {
      ...styles,
      ...props.style
    },
    // @ts-ignore
    as: props.as ?? "p"
  }, props.balance ? /* @__PURE__ */ React8.createElement(Balancer, { as: "span", style: {
    textAlign: props.align
  } }, props.children) : props.children);
};
var Title3 = (props) => {
  let styles = getTextStyles(props);
  return React8.createElement(Element, {
    style: {
      ...styles,
      ...props.style
    },
    // @ts-ignore
    as: props.as ?? "h1"
  }, props.balance ? /* @__PURE__ */ React8.createElement(Balancer, { style: {
    textAlign: props.align
  } }, props.children) : props.children);
};

// src/attributes/index.tsx
var Wrapper = styled5("div").withConfig({
  componentId: "sc-4jzbpy-0"
})`display:grid;grid-template-columns:${({
  $columns
}) => $columns ? `repeat(${$columns}, minmax(0, 1fr))` : "repeat(auto-fill, minmax(var(--width), 1fr))"};gap:1px;background:${theme.colors.gray300};border:1px solid ${theme.colors.gray300};border-radius:10px;overflow:hidden;${({
  $columns
}) => $columns ? `
    @media (max-width: 720px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 520px) {
      grid-template-columns: minmax(0, 1fr);
    }
  ` : ""}`;
var Attribute = styled5("div").withConfig({
  componentId: "sc-4jzbpy-1"
})`background:${theme.colors.background};padding:15px;display:flex;align-items:center;`;
var Inner = styled5("div").withConfig({
  componentId: "sc-4jzbpy-2"
})`display:flex;flex-direction:column;gap:3px;`;
var Attributes = ({
  attributes,
  itemWidth = "300px",
  columns
}) => {
  return /* @__PURE__ */ React9.createElement(Wrapper, { $columns: columns, style: {
    "--width": itemWidth
  } }, attributes.map((attr, i) => /* @__PURE__ */ React9.createElement(Attribute, { key: i }, /* @__PURE__ */ React9.createElement(Inner, null, /* @__PURE__ */ React9.createElement(Text, { weight: "bold", size: "1" }, attr.label), /* @__PURE__ */ React9.createElement(Text, { size: "2", weight: "medium", color: "gray700", as: "div" }, attr.content)))));
};

// src/avatar/index.tsx
import React11, { useRef as useRef3, useState as useState7 } from "react";
import { useInterval } from "react-use";

// src/tooltip/index.tsx
import * as RadixTooltip from "@radix-ui/react-tooltip";
import React10, { useEffect as useEffect4, useState as useState6 } from "react";
import Balancer2 from "react-wrap-balancer";
import { keyframes as keyframes3, styled as styled6 } from "styled-components";
var fadeInTop = keyframes3`
  from { opacity: 0; transform: translateY(-15px) scale(0.75); filter: blur(2px) }
  to { opacity: 1; transform: translateY(0) scale(1); filter: blur(0px) }
`;
var fadeOutTop = keyframes3`
  from { opacity: 1; transform: translateY(0) scale(1); filter: blur(0px) }
  to { opacity: 0; transform: translateY(-15px) scale(0.75); filter: blur(2px) }
`;
var fadeInBottom = keyframes3`
  from { opacity: 0; transform: translateY(15px) scale(0.75); filter: blur(2px) }
  to { opacity: 1; transform: translateY(0) scale(1); filter: blur(0px) }
`;
var fadeOutBottom = keyframes3`
  from { opacity: 1; transform: translateY(0) scale(1); filter: blur(0px) }
  to { opacity: 0; transform: translateY(15px) scale(0.75); filter: blur(2px) }
`;
var fadeInLeft = keyframes3`
  from { opacity: 0; transform: translateX(15px) scale(0.75); filter: blur(2px) }
  to { opacity: 1; transform: translateX(0) scale(1); filter: blur(0px) }
`;
var fadeOutLeft = keyframes3`
  from { opacity: 1; transform: translateX(0) scale(1); filter: blur(0px) }
  to { opacity: 0; transform: translateX(15px) scale(0.75); filter: blur(2px) }
`;
var fadeInRight = keyframes3`
  from { opacity: 0; transform: translateX(-15px) scale(0.75); filter: blur(2px) }
  to { opacity: 1; transform: translateX(0) scale(1); filter: blur(0px) }
`;
var fadeOutRight = keyframes3`
  from { opacity: 1; transform: translateX(0) scale(1); filter: blur(0px) }
  to { opacity: 0; transform: translateX(-15px) scale(0.75); filter: blur(2px) }
`;
var Content6 = styled6(RadixTooltip.Content).withConfig({
  componentId: "sc-2oojj5-0"
})`display:flex;gap:10px;align-items:center;cursor:default;background:${theme.colors.foreground};color:${theme.colors.background};border-radius:7px;box-shadow:${theme.shadows.medium};will-change:transform,opacity;padding:8px 10px;font-size:12px;font-weight:500;max-width:400px;z-index:99999;&[data-state='instant-open'][data-side='top']{animation:${fadeInBottom} 0.2s ease forwards;}&[data-state='delayed-open'][data-side='top']{animation:${fadeInBottom} 0.2s ease forwards;}&[data-state='closed'][data-side='top']{animation:${fadeOutBottom} 0.2s ease forwards;}&[data-state='instant-open'][data-side='bottom']{animation:${fadeInTop} 0.2s ease forwards;}&[data-state='delayed-open'][data-side='bottom']{animation:${fadeInTop} 0.2s ease forwards;}&[data-state='closed'][data-side='bottom']{animation:${fadeOutTop} 0.2s ease forwards;}&[data-state='instant-open'][data-side='left']{animation:${fadeInLeft} 0.2s ease forwards;}&[data-state='delayed-open'][data-side='left']{animation:${fadeInLeft} 0.2s ease forwards;}&[data-state='closed'][data-side='left']{animation:${fadeOutLeft} 0.2s ease forwards;}&[data-state='instant-open'][data-side='right']{animation:${fadeInRight} 0.2s ease forwards;}&[data-state='delayed-open'][data-side='right']{animation:${fadeInRight} 0.2s ease forwards;}&[data-state='closed'][data-side='right']{animation:${fadeOutRight} 0.2s ease forwards;}`;
var Arrow2 = styled6(RadixTooltip.Arrow).withConfig({
  componentId: "sc-2oojj5-1"
})`fill:${theme.colors.foreground};`;
var Tooltip = ({
  content,
  children,
  arrow,
  delayDuration,
  enabled,
  side = "bottom",
  align = "center"
}) => {
  let [disabled, setDisabled] = useState6(true);
  useEffect4(() => {
    setDisabled(false);
  }, []);
  if (enabled === false) return /* @__PURE__ */ React10.createElement(React10.Fragment, null, children);
  return /* @__PURE__ */ React10.createElement(RadixTooltip.Provider, { delayDuration }, /* @__PURE__ */ React10.createElement(RadixTooltip.Root, null, /* @__PURE__ */ React10.createElement(RadixTooltip.Trigger, { asChild: true, disabled }, children), /* @__PURE__ */ React10.createElement(RadixTooltip.Portal, null, /* @__PURE__ */ React10.createElement(Content6, { sideOffset: 5, side, align, hideWhenDetached: true }, typeof content == "string" ? /* @__PURE__ */ React10.createElement(React10.Fragment, null, /* @__PURE__ */ React10.createElement(Balancer2, null, content)) : /* @__PURE__ */ React10.createElement(React10.Fragment, null, content), arrow && /* @__PURE__ */ React10.createElement(Arrow2, null)))));
};

// src/avatar/index.tsx
var loadedSet = /* @__PURE__ */ new Set();
var Avatar = ({
  entity,
  size = 30,
  noTooltip,
  withInitials,
  radius,
  imageFit = "cover"
}) => {
  let src = entity?.photoUrl || entity?.imageUrl;
  let name = entity?.name;
  let ref = useRef3(null);
  let [loaded, setLoaded] = useState7(() => src ? loadedSet.has(src) : false);
  useInterval(() => {
    if (loaded || !ref.current || !src) return;
    if (ref.current.complete) {
      setLoaded(true);
      loadedSet.add(src);
    }
  }, 100);
  let initials = name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  let inner = /* @__PURE__ */ React11.createElement("div", { style: {
    width: size,
    aspectRatio: "1 / 1",
    borderRadius: typeof radius == "number" ? radius : "50%",
    backgroundColor: theme.colors.gray200,
    display: "flex",
    position: "relative",
    overflow: "hidden"
  } }, src && /* @__PURE__ */ React11.createElement("img", { src, alt: name, ref, style: {
    width: "100%",
    height: "100%",
    borderRadius: typeof radius == "number" ? radius : "50%",
    objectFit: imageFit,
    opacity: loaded ? 1 : 0,
    transition: "opacity 0.2s",
    display: "block"
  }, onLoad: () => {
    if (!src) return;
    setLoaded(true);
    loadedSet.add(src);
  } }), initials && withInitials && /* @__PURE__ */ React11.createElement("span", { style: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: "white",
    fontSize: size / 2.5,
    fontWeight: 700,
    userSelect: "none",
    textShadow: "0 0 4px rgba(0, 0, 0, 0.7)"
  } }, initials));
  if (noTooltip) return inner;
  return /* @__PURE__ */ React11.createElement(Tooltip, { content: name }, inner);
};

// src/badge/index.tsx
import React12 from "react";

// src/badge/constants.ts
import { memo as memo2 } from "@lowerdeck/memo";
var badgeColor = memo2((color) => ({
  color: theme.colors[getColorKey(color, "900")],
  backgroundColor: theme.colors[getColorKey(color, "300")]
}));
var sizes = {
  "1": {
    padding: "2px 6px",
    fontSize: 10,
    borderRadius: 4
  },
  "2": {
    padding: "4px 10px",
    fontSize: 14,
    borderRadius: 6
  },
  "3": {
    padding: "8px 16px",
    fontSize: 20,
    borderRadius: 8
  }
};
var getBadgeStyles = ({
  color,
  size = "2"
}) => ({
  ...badgeColor(color),
  ...sizes[size]
});

// src/badge/index.tsx
var Badge = ({
  children,
  color,
  size,
  style
}) => {
  let styles = getBadgeStyles({
    color,
    size
  });
  return /* @__PURE__ */ React12.createElement("span", { style: {
    ...styles,
    ...style,
    display: "inline-flex",
    alignItems: "center",
    fontWeight: 600
  } }, children);
};

// src/button/index.tsx
import { AnimatePresence, motion as motion4 } from "framer-motion";
import React14, { useEffect as useEffect5, useRef as useRef4, useState as useState8 } from "react";
import { Check } from "react-feather";
import { styled as styled9 } from "styled-components";
import _useDelayed from "use-delayed";

// src/spinner/index.tsx
import React13 from "react";
import { keyframes as keyframes4, styled as styled7 } from "styled-components";
var spinnerTheme = createTheme({
  duration: createThemeToken(),
  size: createThemeToken(),
  foreground: createThemeToken(),
  background: createThemeToken()
});
var rotate = keyframes4`
  0% { transform: rotate(0deg) }
  100% { transform: rotate(360deg) }
`;
var Wrapper2 = styled7("div").withConfig({
  componentId: "sc-xlc9nt-0"
})`display:inline-block;animation:${rotate} ${spinnerTheme.duration} ease infinite;border:calc(${spinnerTheme.size} / 7) solid ${spinnerTheme.background};border-top-color:${spinnerTheme.foreground};border-radius:50%;width:${spinnerTheme.size};height:${spinnerTheme.size};`;
var Spinner = ({
  size,
  foreground,
  background,
  duration,
  center
}) => {
  if (typeof size == "number") size = `${size}px`;
  return /* @__PURE__ */ React13.createElement(Wrapper2, { "aria-live": "polite", title: "Loading...", style: {
    ...spinnerTheme.setRootStyles({
      duration: duration ?? "900ms",
      size: (size ?? "26px").toString(),
      foreground: foreground ?? theme.colors.black500,
      background: background ?? theme.colors.gray300
    }),
    margin: center ? "0px auto" : void 0
  } });
};
var CenteredSpinner = (d) => /* @__PURE__ */ React13.createElement("div", { style: {
  display: "flex",
  justifyContent: "center"
} }, /* @__PURE__ */ React13.createElement(Spinner, { ...d, center: true }));

// src/button/constants.ts
import { memo as memo3 } from "@lowerdeck/memo";

// src/theme/gradient.ts
var linearGradient = (angle, ...colors2) => {
  return `linear-gradient(${angle}deg, ${colors2.join(", ")})`;
};

// src/button/constants.ts
var coloredButtonVariants = memo3((color) => ({
  solid: {
    passive: {
      color: getForegroundColor(getColorKey(color, "900")),
      background: theme.colors[getColorKey(color, "700")],
      border: theme.colors[getColorKey(color, "700")],
      shadow: theme.shadows.small
    },
    active: {
      color: getForegroundColor(getColorKey(color, "900")),
      background: theme.colors[getColorKey(color, "700")],
      border: theme.colors[getColorKey(color, "700")],
      shadow: theme.shadows.medium
    },
    spinner: {
      foreground: theme.colors.background,
      background: theme.colors[getColorKey(color, "400")]
    },
    animateClickScale: true
  },
  outline: {
    passive: {
      color: theme.colors[getColorKey(color, "800")],
      background: "transparent",
      border: theme.colors[getColorKey(color, "600")],
      shadow: "none"
    },
    active: {
      color: theme.colors[getColorKey(color, "800")],
      background: "transparent",
      border: theme.colors[getColorKey(color, "700")],
      shadow: "none"
    },
    spinner: {
      foreground: theme.colors[getColorKey(color, "900")],
      background: theme.colors[getColorKey(color, "200")]
    },
    animateClickScale: false
  },
  soft: {
    passive: {
      color: theme.colors[getColorKey(color, "800")],
      background: theme.colors[getColorKey(color, "100")],
      border: theme.colors[getColorKey(color, "100")],
      shadow: "none"
    },
    active: {
      color: theme.colors[getColorKey(color, "800")],
      background: theme.colors[getColorKey(color, "200")],
      border: theme.colors[getColorKey(color, "200")],
      shadow: "none"
    },
    spinner: {
      foreground: theme.colors[getColorKey(color, "900")],
      background: theme.colors[getColorKey(color, "200")]
    },
    animateClickScale: false
  },
  ghost: {
    passive: {
      color: theme.colors[getColorKey(color, "800")],
      background: "transparent",
      border: "transparent",
      shadow: "none"
    },
    active: {
      color: theme.colors[getColorKey(color, "900")],
      background: "transparent",
      border: "transparent",
      shadow: "none"
    },
    spinner: {
      foreground: theme.colors[getColorKey(color, "900")],
      background: theme.colors[getColorKey(color, "200")]
    },
    animateClickScale: false
  }
}));
var monoChromeButtonVariants = {
  solid: {
    passive: {
      color: theme.colors.background,
      background: linearGradient(0, theme.colors.gray800, theme.colors.gray900),
      border: "transparent",
      shadow: theme.shadows.small
    },
    active: {
      color: theme.colors.background,
      background: linearGradient(0, theme.colors.gray800, theme.colors.gray900),
      border: "transparent",
      shadow: theme.shadows.medium
    },
    spinner: {
      foreground: theme.colors.background,
      background: theme.colors.gray700
    },
    animateClickScale: true
  },
  outline: {
    passive: {
      color: theme.colors.foreground,
      background: "transparent",
      border: theme.colors.gray300,
      shadow: "none"
    },
    active: {
      color: theme.colors.foreground,
      background: "transparent",
      border: theme.colors.gray400,
      shadow: "none"
    },
    spinner: {
      foreground: theme.colors.foreground,
      background: theme.colors.gray100
    },
    animateClickScale: false
  },
  soft: {
    passive: {
      color: theme.colors.foreground,
      background: theme.colors.gray200,
      border: theme.colors.gray100,
      shadow: "none"
    },
    active: {
      color: theme.colors.foreground,
      background: theme.colors.gray300,
      border: theme.colors.gray200,
      shadow: "none"
    },
    spinner: {
      foreground: theme.colors.foreground,
      background: theme.colors.gray200
    },
    animateClickScale: false
  },
  ghost: {
    passive: {
      color: theme.colors.gray600,
      background: "transparent",
      border: "transparent",
      shadow: "none"
    },
    active: {
      color: theme.colors.gray700,
      background: "transparent",
      border: "transparent",
      shadow: "none"
    },
    spinner: {
      foreground: theme.colors.foreground,
      background: theme.colors.gray200
    },
    animateClickScale: false
  }
};
var sizes2 = {
  "1": {
    gap: "6px",
    height: "26px",
    padding: "0 8px",
    borderRadius: "6px",
    fontSize: "12px"
  },
  "2": {
    gap: "8px",
    height: "30px",
    padding: "0 10px",
    borderRadius: "8px",
    fontSize: "14px"
  },
  "3": {
    gap: "10px",
    height: "36px",
    padding: "0 12px",
    borderRadius: "10px",
    fontSize: "16px"
  },
  "4": {
    gap: "12px",
    height: "42px",
    padding: "0 14px",
    borderRadius: "12px",
    fontSize: "18px"
  },
  "5": {
    gap: "14px",
    height: "48px",
    padding: "0 16px",
    borderRadius: "14px",
    fontSize: "20px"
  }
};
var buttonTheme = createTheme({
  passive_color: createThemeToken(),
  passive_background: createThemeToken(),
  passive_border: createThemeToken(),
  passive_shadow: createThemeToken(),
  active_color: createThemeToken(),
  active_background: createThemeToken(),
  active_border: createThemeToken(),
  active_shadow: createThemeToken(),
  active_transform: createThemeToken(),
  spinner_foreground: createThemeToken(),
  spinner_background: createThemeToken()
});
var getColorButtonTheme = memo3((color, variant) => {
  let v = (color ? coloredButtonVariants(color) : monoChromeButtonVariants)[variant];
  return buttonTheme.setRootStyles({
    passive_color: v.passive.color,
    passive_background: v.passive.background,
    passive_border: v.passive.border,
    passive_shadow: v.passive.shadow,
    active_color: v.active.color,
    active_background: v.active.background,
    active_border: v.active.border,
    active_shadow: v.active.shadow,
    active_transform: v.animateClickScale ? "scale(0.98)" : "none",
    spinner_foreground: v.spinner.foreground,
    spinner_background: v.spinner.background
  });
});
var getButtonStyles = (props) => {
  let size = sizes2[props.size ?? "3"];
  if (props.variant == "ghost") {
    size = {
      ...size,
      padding: "0",
      height: "auto"
    };
  }
  return {
    margin: 0,
    fontFamily: "inherit",
    ...size,
    ...getColorButtonTheme(props.color, props.variant ?? "solid")
  };
};
var getButtonSize = (size) => sizes2[size];

// src/button/linkButton.ts
import { styled as styled8 } from "styled-components";
var LinkButton = styled8("button").attrs({
  type: "button"
}).withConfig({
  componentId: "sc-16wmsg7-0"
})`background:none;border:none;cursor:pointer;font-size:inherit;font-weight:inherit;color:inherit;padding:0;text-decoration:underline;transition:color 0.2s;line-height:inherit;`;

// src/button/index.tsx
var useDelayed = _useDelayed;
if (useDelayed.default) useDelayed = useDelayed.default;
var Wrapper3 = styled9("button").withConfig({
  componentId: "sc-1gmpd00-0"
})`display:inline-flex;align-items:center;flex-shrink:0;position:relative;cursor:pointer;color:${buttonTheme.passive_color};background:${buttonTheme.passive_background};border:solid 1px ${buttonTheme.passive_border};box-shadow:${buttonTheme.passive_shadow};transition:all 0.2s;user-select:none;&:hover,&:focus{color:${buttonTheme.active_color};background:${buttonTheme.active_background};border:solid 1px ${buttonTheme.active_border};box-shadow:${buttonTheme.active_shadow};}&:active{transform:${buttonTheme.active_transform};}&:disabled{cursor:not-allowed;opacity:0.7;}`;
var Overlay3 = styled9(motion4.div).withConfig({
  componentId: "sc-1gmpd00-1"
})`position:absolute;display:flex;align-items:center;justify-content:center;top:0;left:0;right:0;bottom:0;`;
var OverlayInner = styled9(motion4.div).withConfig({
  componentId: "sc-1gmpd00-2"
})`display:flex;align-items:center;justify-content:center;`;
var Inner2 = styled9("div").withConfig({
  componentId: "sc-1gmpd00-3"
})`display:inline-flex;align-items:center;justify-content:center;flex-grow:1;flex-shrink:0;gap:6px;`;
var Icon = styled9("span").withConfig({
  componentId: "sc-1gmpd00-4"
})`display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;svg{height:16px;width:16px;}`;
var Content7 = styled9("span").withConfig({
  componentId: "sc-1gmpd00-5"
})`display:inline-block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;line-height:1.5;font-weight:500;`;
var Button = React14.forwardRef(({
  children,
  iconLeft,
  iconRight,
  loading,
  success,
  fullWidth,
  variant,
  color,
  size,
  onHover,
  as = "button",
  ...props
}, ref) => {
  let [isHovered, setIsHovered] = useState8(false);
  useEffect5(() => onHover?.(), [isHovered]);
  let isMouseOverRef = useRef4(false);
  let style = getButtonStyles({
    color,
    variant,
    size
  });
  let loadingVisible = useDelayed(loading, 200, [false]);
  let successVisible = useDelayed(success, 200, [false]);
  let contentVisible = useDelayed(!loading && !success, 200);
  return /* @__PURE__ */ React14.createElement(Wrapper3, { as, ref, ...props, style: {
    ...style,
    ...props.style,
    width: fullWidth ? "100%" : void 0,
    ...!children && {
      width: style.height,
      padding: 0
    }
  }, disabled: props.disabled || loading, onClick: (e) => {
    if (loading || success) e.preventDefault();
    else props.onClick?.(e);
  }, onMouseEnter: () => {
    isMouseOverRef.current = true;
    setTimeout(() => {
      if (isMouseOverRef.current) setIsHovered(true);
    }, 100);
  }, onMouseLeave: () => {
    isMouseOverRef.current = false;
    setIsHovered(false);
  } }, /* @__PURE__ */ React14.createElement(AnimatePresence, null, loadingVisible && !successVisible && /* @__PURE__ */ React14.createElement(Overlay3, { key: "loading", initial: {
    opacity: 0,
    scale: 0.9
  }, animate: {
    opacity: 1,
    scale: 1
  }, exit: {
    opacity: 0,
    scale: 0.9
  }, style: {
    backgroundColor: variant == "outline" ? void 0 : "rgba(0, 0, 0, 0.1)"
  }, transition: {
    duration: 0.2,
    ease: "easeInOut"
  } }, /* @__PURE__ */ React14.createElement(OverlayInner, null, /* @__PURE__ */ React14.createElement(Spinner, { size: 16, foreground: buttonTheme.spinner_foreground, background: buttonTheme.spinner_background }))), successVisible && /* @__PURE__ */ React14.createElement(Overlay3, { key: "success", initial: {
    opacity: 0,
    scale: 0.7
  }, animate: {
    opacity: 1,
    scale: 1
  }, exit: {
    opacity: 0,
    scale: 0.7
  }, style: {
    backgroundColor: variant == "outline" ? void 0 : "rgba(0, 0, 0, 0.1)"
  }, transition: {
    duration: 0.3,
    ease: "easeInOut"
  } }, /* @__PURE__ */ React14.createElement(OverlayInner, { initial: {
    scale: 0.5,
    rotate: -15
  }, animate: {
    scale: 1,
    rotate: -10
  }, transition: {
    delay: 0.1
  } }, /* @__PURE__ */ React14.createElement(Icon, null, /* @__PURE__ */ React14.createElement(Check, { size: 16, strokeWidth: 3 }))))), /* @__PURE__ */ React14.createElement(Inner2, { style: {
    opacity: contentVisible ? 1 : 0,
    transition: "opacity 0.25s ease",
    textAlign: fullWidth || !children ? "center" : "left",
    justifyContent: fullWidth || !children ? "center" : "flex-start"
  }, "aria-hidden": loading || success }, iconLeft && /* @__PURE__ */ React14.createElement(Icon, null, iconLeft), children && /* @__PURE__ */ React14.createElement(Content7, null, children), iconRight && /* @__PURE__ */ React14.createElement(Icon, null, iconRight)));
});

// src/callout/index.tsx
import { RiAlertLine } from "@remixicon/react";
import React15 from "react";
import { styled as styled10 } from "styled-components";

// src/callout/constants.ts
import { memo as memo4 } from "@lowerdeck/memo";
var calloutColor = memo4((color) => ({
  color: theme.colors[getColorKey(color, "900")],
  backgroundColor: theme.colors[getColorKey(color, "300")]
}));
var sizes3 = {
  "1": {
    padding: "12px 16px",
    fontSize: 12,
    borderRadius: 10,
    gap: 10,
    icon: {
      size: 16
    }
  },
  "2": {
    padding: "16px 24px",
    fontSize: 14,
    borderRadius: 12,
    gap: 14,
    icon: {
      size: 18
    }
  },
  "3": {
    padding: "20px 32px",
    fontSize: 16,
    borderRadius: 14,
    gap: 18,
    icon: {
      size: 20
    }
  }
};
var getCalloutStyles = ({
  color,
  size = "2"
}) => ({
  ...calloutColor(color),
  ...sizes3[size]
});

// src/callout/index.tsx
var Wrapper4 = styled10("div").withConfig({
  componentId: "sc-56dyzz-0"
})`display:flex;align-items:center;`;
var Icon2 = styled10("div").withConfig({
  componentId: "sc-56dyzz-1"
})`display:flex;align-items:center;justify-content:center;svg{height:100%;aspect-ratio:1;}`;
var Content8 = styled10("p").withConfig({
  componentId: "sc-56dyzz-2"
})`display:flex;align-items:center;flex-grow:1;`;
var Callout = ({
  children,
  color,
  size,
  style
}) => {
  let {
    icon,
    ...styles
  } = getCalloutStyles({
    color,
    size
  });
  return /* @__PURE__ */ React15.createElement(Wrapper4, { style: {
    ...styles,
    ...style,
    display: "flex",
    alignItems: "center"
  } }, /* @__PURE__ */ React15.createElement(Icon2, { style: {
    width: icon.size,
    height: icon.size
  } }, /* @__PURE__ */ React15.createElement(RiAlertLine, null)), /* @__PURE__ */ React15.createElement(Content8, null, children));
};

// src/cardLinks/index.tsx
import { RiArrowRightSLine } from "@remixicon/react";
import React17 from "react";
import { styled as styled11 } from "styled-components";

// src/link.tsx
import React16 from "react";
var defaultLink = ({
  to,
  children,
  className
}) => {
  return /* @__PURE__ */ React16.createElement("a", { href: to, className }, children);
};
var getLink = () => {
  let linkFromWindow = typeof window !== "undefined" ? window.LinkComponent : void 0;
  let linkFromGlobal = typeof global !== "undefined" ? global.LinkComponent : void 0;
  return linkFromWindow ?? linkFromGlobal ?? defaultLink;
};

// src/cardLinks/index.tsx
var LinkWrapper = styled11("span").withConfig({
  componentId: "sc-3fd4jm-0"
})`padding:20px;border-radius:10px;border:solid 1px ${theme.colors.gray300};display:flex;gap:10px;transition:all 0.2s;flex-grow:1;cursor:pointer;background:${theme.colors.background};&:hover,&:focus{border-color:${theme.colors.black100} !important;transform:scale(1.02);box-shadow:${theme.shadows.medium};}&:active{transform:scale(0.98);}`;
var LinkContent = styled11("div").withConfig({
  componentId: "sc-3fd4jm-1"
})`display:flex;flex-direction:column;gap:4px;flex-grow:1;`;
var LinkTitle = styled11("p").withConfig({
  componentId: "sc-3fd4jm-2"
})`font-size:15px;font-weight:600;color:${theme.colors.foreground};`;
var LinkDescription = styled11("p").withConfig({
  componentId: "sc-3fd4jm-3"
})`font-size:12px;font-weight:500;color:${theme.colors.gray600};`;
var Icon3 = styled11("div").withConfig({
  componentId: "sc-3fd4jm-4"
})`display:flex;align-items:center;justify-content:center;flex-shrink:0;color:${theme.colors.gray600};svg{height:20px;width:20px;}`;
var CardLinks = {
  Items: styled11("div").withConfig({
    componentId: "sc-3fd4jm-5"
  })`display:grid;display:flex;flex-direction:column;gap:10px;`,
  Group: styled11("div").withConfig({
    componentId: "sc-3fd4jm-6"
  })`display:flex;flex-direction:column;`,
  GroupPanel: styled11("div").withConfig({
    componentId: "sc-3fd4jm-7"
  })`display:flex;flex-direction:column;padding:13px 10px 10px 10px;border-radius:10px;background:${theme.colors.gray200};box-shadow:${theme.shadows.small};.link{border:solid 1px transparent;}& > h2{font-weight:700 !important;padding:0 4px;}& > p{font-weight:600;color:${theme.colors.gray700};padding:0 4px;}`,
  GroupTitle: styled11("h2").withConfig({
    componentId: "sc-3fd4jm-8"
  })`font-size:18px;font-weight:600;margin-bottom:10px;`,
  GroupPreTitle: styled11("p").withConfig({
    componentId: "sc-3fd4jm-9"
  })`font-size:14px;font-weight:600;margin-bottom:4px;color:${theme.colors.gray700};`,
  Link: ({
    to,
    title,
    description
  }) => {
    let Link = getLink();
    return /* @__PURE__ */ React17.createElement(Link, { to, style: {
      textDecoration: "none",
      color: "inherit",
      display: "flex",
      flexDirection: "column"
    } }, /* @__PURE__ */ React17.createElement(LinkWrapper, { className: "link" }, /* @__PURE__ */ React17.createElement(LinkContent, null, /* @__PURE__ */ React17.createElement(LinkTitle, null, title), /* @__PURE__ */ React17.createElement(LinkDescription, null, description)), /* @__PURE__ */ React17.createElement(Icon3, null, /* @__PURE__ */ React17.createElement(RiArrowRightSLine, null))));
  }
};

// src/checkbox/index.tsx
import * as RadixCheckbox from "@radix-ui/react-checkbox";
import * as VisuallyHidden2 from "@radix-ui/react-visually-hidden";
import clsx from "clsx";
import React20, { useEffect as useEffect6, useId as useId2, useState as useState9 } from "react";
import { Check as Check2 } from "react-feather";
import { keyframes as keyframes5, styled as styled14 } from "styled-components";

// src/input/index.tsx
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import React19, { useId } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { styled as styled13 } from "styled-components";

// src/error/index.tsx
import { RiErrorWarningLine } from "@remixicon/react";
import React18 from "react";
import { styled as styled12 } from "styled-components";

// src/theme/calc.ts
var calc = {
  add: (a, b) => `calc(${a} + ${b})`,
  subtract: (a, b) => `calc(${a} - ${b})`,
  multiply: (a, b) => `calc(${a} * ${b})`,
  divide: (a, b) => `calc(${a} / ${b})`
};

// src/error/index.tsx
var Wrapper5 = styled12("p").withConfig({
  componentId: "sc-1n1or6r-0"
})`display:flex;gap:7px;align-items:center;color:${theme.colors.red900};font-weight:500;`;
var Error = ({
  size,
  children,
  style
}) => {
  let sizeString = (typeof size == "number" ? `${size}px` : size) || "14px";
  let inner = children;
  if (typeof children == "string" && children.startsWith("[") && children.includes("]")) {
    let endIndex = children.indexOf("]");
    inner = children.substring(endIndex + 1);
  }
  return /* @__PURE__ */ React18.createElement(Wrapper5, { style: {
    fontSize: sizeString,
    ...style
  } }, /* @__PURE__ */ React18.createElement("span", { style: {
    display: "inline-flex"
  } }, /* @__PURE__ */ React18.createElement(RiErrorWarningLine, { size: calc.multiply(sizeString, 1.3) })), /* @__PURE__ */ React18.createElement("span", null, inner));
};

// src/input/index.tsx
var Wrapper6 = styled13("div").withConfig({
  componentId: "sc-1b88xuj-0"
})`display:flex;flex-direction:column;align-items:flex-start;width:100%;`;
var InputLabel = styled13("label").withConfig({
  componentId: "sc-1b88xuj-1"
})`font-size:12px;color:${theme.colors.gray900};margin-bottom:5px;font-weight:500;user-select:none;white-space:nowrap;`;
var InputDescription = styled13("p").withConfig({
  componentId: "sc-1b88xuj-2"
})`font-size:12px;color:${theme.colors.gray600};margin-bottom:8px;user-select:none;`;
var InputWrapper = styled13("div").withConfig({
  componentId: "sc-1b88xuj-3"
})`outline:1px solid transparent;background:${theme.colors.gray300};color:${theme.colors.foreground};display:flex;width:100%;transition:all 0.3s ease;gap:8px;align-items:center;&:focus-within{background:${theme.colors.gray400};outline:1px solid ${theme.colors.gray600};}`;
var InputField = styled13("input").withConfig({
  componentId: "sc-1b88xuj-4"
})`flex-grow:1;border:none;background:transparent;height:100%;outline:none;&::placeholder{color:${theme.colors.gray700};}&[type='number']{-moz-appearance:textfield;}&::-webkit-outer-spin-button,&::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;}`;
var TextareaField = styled13(TextareaAutosize).withConfig({
  componentId: "sc-1b88xuj-5"
})`flex-grow:1;border:none;background:transparent;height:100%;width:100%;outline:none;resize:none;&::placeholder{color:${theme.colors.gray700};}`;
var ColorSwatchInput = styled13("input").withConfig({
  componentId: "sc-1b88xuj-6"
})`appearance:none;-webkit-appearance:none;-moz-appearance:none;border:none;background:none;cursor:pointer;padding:0px;border-radius:5px;&::-webkit-color-swatch-wrapper{padding:0;}&::-webkit-color-swatch{border:none;border-radius:5px;}`;
var Input = ({
  style,
  size = "3",
  label,
  description,
  hideLabel,
  error,
  as = "input",
  minRows,
  maxRows,
  onInput,
  type = "text",
  ...props
}) => {
  let sizeStyles = getButtonSize(size);
  let id = useId();
  return /* @__PURE__ */ React19.createElement(Wrapper6, { style }, hideLabel ? /* @__PURE__ */ React19.createElement(VisuallyHidden, null, /* @__PURE__ */ React19.createElement(InputLabel, { htmlFor: id }, label)) : /* @__PURE__ */ React19.createElement(InputLabel, { htmlFor: id }, label), description && /* @__PURE__ */ React19.createElement(InputDescription, null, description), /* @__PURE__ */ React19.createElement(InputWrapper, { style: {
    height: as == "textarea" ? void 0 : sizeStyles.height,
    borderRadius: sizeStyles.borderRadius,
    outline: error ? `1px solid ${theme.colors.red900}` : void 0,
    paddingBlock: as == "textarea" ? calc.divide(sizeStyles.height, 3.7) : void 0
  } }, type == "color" && /* @__PURE__ */ React19.createElement("div", { style: {
    paddingLeft: sizeStyles.padding
  } }, /* @__PURE__ */ React19.createElement(ColorSwatchInput, { type: "color", onChange: (e) => {
    onInput?.(e.target.value, e);
    props.onChange?.(e);
  }, value: props.value, style: {
    height: `calc(${sizeStyles.height} * 0.6)`,
    width: `calc(${sizeStyles.height} * 0.6)`
  } })), as == "textarea" ? /* @__PURE__ */ React19.createElement(TextareaField, { id, minRows, maxRows, style: {
    padding: sizeStyles.padding,
    fontSize: "14px",
    height: style?.height
  }, onChange: (e) => {
    onInput?.(e.target.value, e);
    props.onChange?.(e);
  }, type: type == "color" ? "text" : type, ...props }) : /* @__PURE__ */ React19.createElement(InputField, { id, style: {
    padding: sizeStyles.padding,
    height: sizeStyles.height,
    fontSize: "14px"
  }, onChange: (e) => {
    onInput?.(e.target.value, e);
    props.onChange?.(e);
  }, type: type == "color" ? "text" : type, ...props })), typeof error == "string" && /* @__PURE__ */ React19.createElement(Error, { size: 12, style: {
    marginTop: 6
  } }, error));
};

// src/checkbox/index.tsx
var fadeIn2 = keyframes5`
  from {
    transform: scale(0) rotate(0deg);
    opacity: 0;
  }

  to {
    transform: scale(1) rotate(-10deg);
    opacity: 1;
  }
`;
var fadeOut2 = keyframes5`
  from {
    transform: scale(1);
    opacity: 1;
  }

  to {
    transform: scale(0.5);
    opacity: 0;
  }
`;
var Wrapper7 = styled14("div").withConfig({
  componentId: "sc-sme67s-0"
})`display:flex;align-items:center;cursor:pointer;width:100%;`;
var Root6 = styled14(RadixCheckbox.Root).withConfig({
  componentId: "sc-sme67s-1"
})`display:flex;height:20px;width:20px;border:none;padding:0px;justify-content:center;align-items:center;background:${theme.colors.gray100};border:1px solid ${theme.colors.gray300};border-radius:5px;color:${theme.colors.gray100};transition:all 0.3s ease;position:relative;overflow:hidden;flex-shrink:0;&.disabled{opacity:0.5;}&.readonly{opacity:1;}&::before{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:0px;height:0px;content:'';border-radius:50%;background:${theme.colors.gray800};transition:all 0.3s ease;}&[data-state='checked']{box-shadow:${theme.shadows.small};&::before{width:30px;height:30px;}}`;
var Indicator2 = styled14(RadixCheckbox.Indicator).withConfig({
  componentId: "sc-sme67s-2"
})`display:flex;justify-content:center;align-items:center;&[data-animate='true'][data-state='checked']{animation:${fadeIn2} 0.4s ease forwards;}&[data-animate='true'][data-state='unchecked']{animation:${fadeOut2} 0.3s ease forwards;}&[data-animate='false'][data-state='checked']{transform:scale(1) rotate(-10deg) !important;opacity:1 !important;}`;
var Checkbox = ({
  checked,
  onCheckedChange,
  description,
  label,
  disabled,
  hideLabel,
  readOnly
}) => {
  let [animate, setAnimate] = useState9(false);
  let [initialValue] = useState9(() => checked);
  useEffect6(() => {
    if (initialValue != checked) setAnimate(true);
  }, [checked, initialValue]);
  let id = useId2();
  return /* @__PURE__ */ React20.createElement(React20.Fragment, null, /* @__PURE__ */ React20.createElement(Wrapper7, { style: {
    cursor: readOnly ? "default" : void 0
  } }, /* @__PURE__ */ React20.createElement(Root6, { className: clsx({
    disabled,
    readonly: readOnly
  }), id, checked, onCheckedChange: readOnly ? void 0 : onCheckedChange, disabled: disabled || readOnly }, /* @__PURE__ */ React20.createElement(Indicator2, { "data-animate": animate }, /* @__PURE__ */ React20.createElement(Check2, { size: 12, strokeWidth: 3 }))), !hideLabel ? /* @__PURE__ */ React20.createElement(InputLabel, { htmlFor: id, style: {
    margin: 0,
    width: "100%",
    minHeight: 20,
    paddingLeft: 10,
    display: "flex",
    alignItems: "center",
    cursor: readOnly ? "default" : "pointer"
  } }, label) : /* @__PURE__ */ React20.createElement(VisuallyHidden2.Root, null, /* @__PURE__ */ React20.createElement("label", { htmlFor: id }, label))), description && /* @__PURE__ */ React20.createElement(InputDescription, { style: {
    marginTop: 5,
    marginBottom: 0
  } }, description));
};

// src/control/index.tsx
import * as RadixToggle from "@radix-ui/react-toggle-group";
import React21, { memo as memo5, useEffect as useEffect7, useReducer, useState as useState10 } from "react";
import { styled as styled15 } from "styled-components";
var Root8 = styled15(RadixToggle.Root).withConfig({
  componentId: "sc-1q4kxxc-0"
})`background:${theme.colors.gray300};border-radius:10px;width:fit-content;padding:3px;`;
var List = styled15("div").withConfig({
  componentId: "sc-1q4kxxc-1"
})`display:flex;position:relative;`;
var Item4 = styled15(RadixToggle.Item).attrs({
  type: "button"
}).withConfig({
  componentId: "sc-1q4kxxc-2"
})`display:flex;align-items:center;border:none;background:transparent;padding:0px;font-size:14px;color:${theme.colors.gray600};transition:all 0.3s ease;position:relative;z-index:2;font-weight:500;&:hover{color:${theme.colors.blue900};}&:focus{color:${theme.colors.blue900};}&[data-state='active']{color:${theme.colors.gray100};}`;
var ItemInner = styled15("div").withConfig({
  componentId: "sc-1q4kxxc-3"
})`height:30px;padding:0px 10px;display:flex;align-items:center;justify-content:center;`;
var Indicator3 = styled15("div").withConfig({
  componentId: "sc-1q4kxxc-4"
})`position:absolute;border-radius:7px;z-index:1;background:${theme.colors.blue900};transition:all 0.2s cubic-bezier(0.34,1.56,0.64,1);`;
var Control = memo5(({
  items,
  value,
  onChange
}) => {
  let [elements, setElements] = useState10([]);
  let currentItemIndex = items.findIndex((item) => item.id == value);
  let [, rerender] = useReducer((s) => s + 1, 0);
  useEffect7(() => {
    setTimeout(() => {
      rerender();
      setTimeout(() => {
        rerender();
      }, 400);
    }, 100);
  }, []);
  return /* @__PURE__ */ React21.createElement(Root8, { value, onValueChange: (v) => {
    if (v == value || !v) return;
    onChange?.(v);
  }, type: "single" }, /* @__PURE__ */ React21.createElement(List, null, items.map((item, i) => /* @__PURE__ */ React21.createElement(Item4, { value: item.id, key: i, "data-state": item.id == value ? "active" : "inactive", ref: (el) => {
    if (el) {
      setElements((e) => {
        e[i] = el;
        return e;
      });
    }
  } }, /* @__PURE__ */ React21.createElement(ItemInner, null, item.label))), elements[currentItemIndex] && /* @__PURE__ */ React21.createElement(Indicator3, { style: {
    height: 30,
    top: 0,
    left: elements[currentItemIndex].offsetLeft,
    width: elements[currentItemIndex].offsetWidth
  } })));
});

// src/contextMenu/index.tsx
import React22, { useEffect as useEffect8, useState as useState11 } from "react";
import { createPortal } from "react-dom";
import { keyframes as keyframes6, styled as styled16 } from "styled-components";
var fadeInBottom2 = keyframes6`
  from { opacity: 0; transform: translateY(-10px) scale(0.99); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;
var fadeOutBottom2 = keyframes6`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-10px); }
`;
var Trigger4 = styled16.div.withConfig({
  componentId: "sc-1g6i9gp-0"
})`display:contents;`;
var Content9 = styled16.div.withConfig({
  componentId: "sc-1g6i9gp-1"
})`position:fixed;display:flex;flex-direction:column;transition:all 0.3s ease;padding:5px;color:${theme.colors.background};box-shadow:${theme.shadows.large};background:${theme.colors.foreground};border-radius:10px;min-width:200px;gap:5px;animation:${(p) => p.$closing ? fadeOutBottom2 : fadeInBottom2} 0.2s ease forwards;`;
var Separator = styled16.div.withConfig({
  componentId: "sc-1g6i9gp-2"
})`height:1px;background:${theme.colors.gray800};`;
var Item5 = styled16.button.withConfig({
  componentId: "sc-1g6i9gp-3"
})`font-size:14px;font-weight:500;display:flex;flex-direction:column;justify-content:center;text-align:left;gap:5px;padding:7px 13px;border-radius:6px;outline:none;border:none;background:transparent;transition:all 0.3s ease;color:${theme.colors.background};max-width:250px;&:not(:disabled){&:hover{background:${theme.colors.gray800};}&:focus{background:${theme.colors.gray800};}}&:disabled{opacity:0.7;}h1{font-size:14px;font-weight:500;color:${theme.colors.background};}p{font-size:12px;color:${theme.colors.gray600};}`;
var Title4 = styled16.div.withConfig({
  componentId: "sc-1g6i9gp-4"
})`font-size:14px;font-weight:600;color:${theme.colors.gray100};padding:7px 13px;border-radius:6px;background:transparent;transition:all 0.3s ease;display:flex;align-items:center;gap:5px;`;
var isSeparator = (item) => "type" in item && item.type == "separator";
var ContextMenu = ({
  children,
  disabled,
  label,
  onItemClick,
  items,
  title,
  setIsOpen
}) => {
  let [open, setOpen] = useState11(false);
  let [closing, setClosing] = useState11(false);
  let [position, setPosition] = useState11({
    x: 0,
    y: 0
  });
  let zIndex = useDialogZIndex(open);
  let close = () => {
    setClosing(true);
    setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, 200);
  };
  useEffect8(() => setIsOpen?.(open), [open]);
  useEffect8(() => {
    if (!open) return;
    let handlePointerDown = () => close();
    let handleClose = () => close();
    let handleKeyDown = (e) => {
      if (e.key == "Escape") close();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("resize", handleClose);
    window.addEventListener("scroll", handleClose, true);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("resize", handleClose);
      window.removeEventListener("scroll", handleClose, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);
  return /* @__PURE__ */ React22.createElement(Trigger4, { "aria-label": label, onContextMenu: (e) => {
    if (disabled) return;
    e.preventDefault();
    setClosing(false);
    setPosition({
      x: e.clientX,
      y: e.clientY
    });
    setOpen(true);
  } }, children, open ? createPortal(/* @__PURE__ */ React22.createElement(Content9, { $closing: closing, onContextMenu: (e) => e.preventDefault(), onPointerDown: (e) => e.stopPropagation(), role: "menu", style: {
    left: position.x,
    top: position.y,
    zIndex
  } }, title && /* @__PURE__ */ React22.createElement(React22.Fragment, null, /* @__PURE__ */ React22.createElement(Title4, null, title), /* @__PURE__ */ React22.createElement(Separator, null)), items.map((item, i) => isSeparator(item) ? /* @__PURE__ */ React22.createElement(Separator, { key: i, role: "separator" }) : /* @__PURE__ */ React22.createElement(Item5, { disabled: item.disabled, key: i, onClick: () => {
    if (item.disabled) return;
    onItemClick?.(item.id);
    close();
  }, role: "menuitem", type: "button" }, /* @__PURE__ */ React22.createElement("h1", null, item.label), item.description && /* @__PURE__ */ React22.createElement("p", null, item.description)))), document.body) : null);
};

// src/copy/index.tsx
import { RiCheckLine } from "@remixicon/react";
import copy from "copy-to-clipboard";
import { AnimatePresence as AnimatePresence2, motion as motion5 } from "framer-motion";
import React23, { useRef as useRef5, useState as useState12 } from "react";
import { toast } from "sonner";
import { styled as styled17 } from "styled-components";
var Value = styled17("pre").withConfig({
  componentId: "sc-1w5cxx8-0"
})`white-space:pre-wrap;word-wrap:break-word;margin:0;display:block;font-size:14px;padding:10px;border-radius:8px;background:${theme.colors.gray100};color:${theme.colors.foreground};border:1px solid ${theme.colors.gray300};`;
var CopyOverlay = styled17(motion5.div).withConfig({
  componentId: "sc-1w5cxx8-1"
})`position:absolute;top:0;right:0;left:0;bottom:0;padding:10px;border-radius:8px;background:rgba(255,255,255,0.8);border:1px solid ${theme.colors.gray300};z-index:1;display:flex;justify-content:center;align-items:center;`;
var CopyIcon = styled17(motion5.div).withConfig({
  componentId: "sc-1w5cxx8-2"
})`color:white;background:${theme.colors.gray800};height:26px;width:26px;border-radius:20px;display:flex;justify-content:center;align-items:center;`;
var useCopy = (topVal) => {
  let [copied, setCopied] = useState12(false);
  let topValRef = useRef5(topVal);
  topValRef.current = topVal;
  let doCopy = (innerVal) => {
    let value = innerVal || topValRef.current;
    if (!value) return;
    if (copied) return;
    copy(value);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2e3);
  };
  return {
    copied,
    copy: doCopy
  };
};
var Copy = ({
  value,
  label,
  copyValue
}) => {
  let {
    copied,
    copy: doCopy
  } = useCopy(copyValue ?? value);
  return /* @__PURE__ */ React23.createElement("div", { onClick: (c) => doCopy(), role: "button", onKeyDown: (c) => doCopy(), tabIndex: 0, style: {
    display: "flex",
    flexDirection: "column"
  } }, label && /* @__PURE__ */ React23.createElement(InputLabel, null, label), /* @__PURE__ */ React23.createElement("div", { style: {
    position: "relative"
  } }, /* @__PURE__ */ React23.createElement(Value, null, value), /* @__PURE__ */ React23.createElement(AnimatePresence2, null, copied && /* @__PURE__ */ React23.createElement(CopyOverlay, { initial: {
    opacity: 0
  }, animate: {
    opacity: 1
  }, exit: {
    opacity: 0
  }, transition: {
    duration: 0.2
  } }, /* @__PURE__ */ React23.createElement(CopyIcon, { initial: {
    scale: 0,
    rotate: -30
  }, animate: {
    scale: 1,
    rotate: -10
  }, transition: {
    duration: 0.3,
    delay: 0.1
  } }, /* @__PURE__ */ React23.createElement(RiCheckLine, { size: 12, strokeWidth: 3 }))))));
};

// src/combobox/index.tsx
import { Combobox as BaseCombobox } from "@base-ui/react/combobox";
import { VisuallyHidden as VisuallyHidden3 } from "@radix-ui/react-visually-hidden";
import { RiArrowDownSLine as RiArrowDownSLine2, RiCheckLine as RiCheckLine2 } from "@remixicon/react";
import React24, { useEffect as useEffect9, useId as useId3, useMemo as useMemo3, useRef as useRef6, useState as useState13 } from "react";
import { keyframes as keyframes7, styled as styled18 } from "styled-components";
var fadeIn3 = keyframes7`
  from {
    transform: scale(0.98);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
`;
var Wrapper8 = styled18("div").withConfig({
  componentId: "sc-1rueb2t-0"
})`display:flex;flex-direction:column;align-items:flex-start;width:100%;`;
var InputGroup = styled18(BaseCombobox.InputGroup).withConfig({
  componentId: "sc-1rueb2t-1"
})`outline:1px solid transparent;background:${theme.colors.gray300};color:${theme.colors.foreground};display:flex;width:100%;transition:all 0.3s ease;gap:8px;align-items:center;&:focus-within{background:${theme.colors.gray400};outline:1px solid ${theme.colors.gray600};}&[data-disabled]{opacity:0.6;cursor:not-allowed;}`;
var Input2 = styled18(BaseCombobox.Input).withConfig({
  componentId: "sc-1rueb2t-2"
})`flex-grow:1;border:none;background:transparent;height:100%;outline:none;min-width:0;&::placeholder{color:${theme.colors.gray700};}&:disabled{cursor:not-allowed;}`;
var Trigger5 = styled18(BaseCombobox.Trigger).attrs({
  type: "button"
}).withConfig({
  componentId: "sc-1rueb2t-3"
})`position:absolute;inset:0;display:flex;align-items:center;justify-content:center;border:none;background:transparent;color:${theme.colors.gray700};cursor:pointer;padding:0;opacity:${(d) => d.$loading ? 0 : 1};transform:scale(${(d) => d.$loading ? 0.85 : 1});transition:${(d) => d.$animateIn ? "opacity 0.3s ease 0.08s, transform 0.3s ease 0.08s, color 0.3s ease" : "opacity 0s linear, transform 0s linear, color 0.18s ease"};&:focus{outline:none;color:${theme.colors.gray900};}&:disabled{cursor:not-allowed;}`;
var EndSlot = styled18("div").withConfig({
  componentId: "sc-1rueb2t-4"
})`width:28px;align-self:stretch;position:relative;display:flex;align-items:center;justify-content:center;padding-right:12px;flex-shrink:0;`;
var SpinnerSlot = styled18("div").withConfig({
  componentId: "sc-1rueb2t-5"
})`position:absolute;inset:0;display:flex;align-items:center;justify-content:center;opacity:${(d) => d.$visible ? 1 : 0};transform:scale(${(d) => d.$visible ? 1 : 0.85});transition:opacity 0.3s ease,transform 0.3s ease;pointer-events:none;`;
var Positioner = styled18(BaseCombobox.Positioner).withConfig({
  componentId: "sc-1rueb2t-6"
})`width:var(--anchor-width);z-index:9999;`;
var Popup = styled18(BaseCombobox.Popup).withConfig({
  componentId: "sc-1rueb2t-7"
})`display:flex;flex-direction:column;gap:6px;padding:6px;box-shadow:${theme.shadows.medium};background:${theme.colors.gray200};border:1px solid ${theme.colors.gray400};border-radius:10px;max-height:min(320px,var(--available-height));overflow:hidden;&[data-open]{animation:${fadeIn3} 0.15s ease forwards;}`;
var List2 = styled18(BaseCombobox.List).withConfig({
  componentId: "sc-1rueb2t-8"
})`display:flex;flex-direction:column;gap:4px;overflow:auto;padding-right:2px;`;
var Group = styled18(BaseCombobox.Group).withConfig({
  componentId: "sc-1rueb2t-9"
})`display:flex;flex-direction:column;gap:4px;`;
var GroupLabel = styled18(BaseCombobox.GroupLabel).withConfig({
  componentId: "sc-1rueb2t-10"
})`font-size:11px;font-weight:600;color:${theme.colors.gray700};padding:6px 8px 2px;letter-spacing:0.04em;`;
var Item6 = styled18(BaseCombobox.Item).withConfig({
  componentId: "sc-1rueb2t-11"
})`display:flex;align-items:center;gap:6px;min-height:38px;padding:8px 10px;border-radius:8px;outline:none;cursor:pointer;transition:background 0.2s ease;&:hover,&[data-highlighted]{background:${theme.colors.gray300};}&[data-selected]{background:${theme.colors.gray400};}&[data-disabled]{opacity:0.5;cursor:not-allowed;}`;
var ItemIndicator = styled18(BaseCombobox.ItemIndicator).withConfig({
  componentId: "sc-1rueb2t-12"
})`display:flex;align-items:center;justify-content:center;color:${theme.colors.gray800};`;
var ItemIndicatorSlot = styled18("div").withConfig({
  componentId: "sc-1rueb2t-13"
})`width:20px;height:20px;display:flex;align-items:center;justify-content:center;flex-shrink:0;`;
var ItemContent = styled18("div").withConfig({
  componentId: "sc-1rueb2t-14"
})`min-width:0;display:flex;flex-direction:column;gap:2px;flex:1;`;
var ItemLabel = styled18("div").withConfig({
  componentId: "sc-1rueb2t-15"
})`font-size:14px;font-weight:500;color:${theme.colors.foreground};line-height:1.2;`;
var ItemDescription = styled18("div").withConfig({
  componentId: "sc-1rueb2t-16"
})`font-size:12px;color:${theme.colors.gray700};line-height:1.35;`;
var EmptyState = styled18(BaseCombobox.Empty).withConfig({
  componentId: "sc-1rueb2t-17"
})`font-size:13px;color:${theme.colors.gray700};padding:10px;text-align:center;&:empty{display:none;padding:0;}`;
var Footer = styled18("div").withConfig({
  componentId: "sc-1rueb2t-18"
})`font-size:12px;color:${theme.colors.gray700};padding:4px 8px 2px;`;
var useDebouncedValue = (value, delay) => {
  let [debouncedValue, setDebouncedValue] = useState13(() => value);
  useEffect9(() => {
    let timeout = window.setTimeout(() => setDebouncedValue(value), delay);
    return () => window.clearTimeout(timeout);
  }, [delay, value]);
  return debouncedValue;
};
var normalizeGroups = (items) => {
  if (!items || items.length === 0) return [];
  if ("items" in items[0]) return items;
  return [{
    label: null,
    items
  }];
};
var flattenGroups = (groups) => {
  return groups.flatMap((group) => group.items);
};
var matchesItem = (item, searchQuery) => {
  if (!searchQuery) return true;
  let query = searchQuery.toLocaleLowerCase();
  let searchable = [item.label, ...item.keywords ?? []];
  if (typeof item.description == "string") searchable.push(item.description);
  return searchable.some((value) => value.toLocaleLowerCase().includes(query));
};
var filterGroups = (items, searchQuery) => {
  let groups = normalizeGroups(items);
  if (!searchQuery) return groups;
  return groups.map((group) => ({
    ...group,
    items: group.items.filter((item) => matchesItem(item, searchQuery))
  })).filter((group) => group.items.length > 0);
};
var createLabelCache = (items) => {
  return items.reduce((acc, item) => {
    acc[item.id] = item.label;
    return acc;
  }, {});
};
var ComboboxInner = ({
  items,
  isLoading,
  status,
  empty,
  search,
  setSearch,
  size = "3",
  label,
  description,
  hideLabel,
  error,
  placeholder,
  disabled,
  readOnly,
  required,
  value,
  valueLabel,
  noResultsMessage = "No results found.",
  inputRef,
  onChange,
  onSearchChange,
  renderItem,
  ...inputProps
}) => {
  let generatedId = useId3();
  let {
    id: providedId,
    name,
    onFocus,
    ...restInputProps
  } = inputProps;
  let id = providedId ?? generatedId;
  let sizeStyles = getButtonSize(size);
  let normalizedValue = value ?? null;
  let normalizedGroups = useMemo3(() => normalizeGroups(items), [items]);
  let flatItems = useMemo3(() => flattenGroups(normalizedGroups), [normalizedGroups]);
  let [labelCache, setLabelCache] = useState13(() => createLabelCache(flatItems));
  let selectedItem = flatItems.find((item) => item.id === normalizedValue) ?? null;
  let selectedLabel = selectedItem?.label ?? (normalizedValue ? labelCache[normalizedValue] : void 0) ?? valueLabel ?? "";
  let [open, setOpen] = useState13(false);
  let previousLoading = useRef6(!!isLoading);
  let [animateArrowIn, setAnimateArrowIn] = useState13(false);
  let dialog = useDialogContext();
  let showLoading = open && !!isLoading;
  let clearSelectedLabelSearch = () => {
    if (search !== selectedLabel) return;
    setSearch("");
    onSearchChange?.("");
  };
  useEffect9(() => {
    if (flatItems.length === 0) return;
    setLabelCache((current) => ({
      ...current,
      ...createLabelCache(flatItems)
    }));
  }, [flatItems]);
  useEffect9(() => {
    if (!normalizedValue || !valueLabel) return;
    setLabelCache((current) => {
      if (current[normalizedValue] === valueLabel) return current;
      return {
        ...current,
        [normalizedValue]: valueLabel
      };
    });
  }, [normalizedValue, valueLabel]);
  useEffect9(() => {
    if (open) return;
    setSearch(selectedLabel);
  }, [open, selectedLabel, setSearch]);
  useEffect9(() => {
    let wasLoading = previousLoading.current;
    let isCurrentlyLoading = !!isLoading;
    if (wasLoading && !isCurrentlyLoading) {
      setAnimateArrowIn(true);
      let timeout = window.setTimeout(() => {
        setAnimateArrowIn(false);
      }, 300);
      previousLoading.current = isCurrentlyLoading;
      return () => window.clearTimeout(timeout);
    }
    if (isCurrentlyLoading) {
      setAnimateArrowIn(false);
    }
    previousLoading.current = isCurrentlyLoading;
  }, [isLoading]);
  return /* @__PURE__ */ React24.createElement(Wrapper8, null, label && (hideLabel ? /* @__PURE__ */ React24.createElement(VisuallyHidden3, null, /* @__PURE__ */ React24.createElement(InputLabel, { htmlFor: id }, label)) : /* @__PURE__ */ React24.createElement(InputLabel, { htmlFor: id }, label)), description && /* @__PURE__ */ React24.createElement(InputDescription, null, description), /* @__PURE__ */ React24.createElement(BaseCombobox.Root, { name, value: normalizedValue, itemToStringLabel: (itemValue) => labelCache[itemValue] ?? itemValue, onValueChange: (nextValue) => {
    let nextItem = flatItems.find((item) => item.id === nextValue) ?? null;
    if (nextItem) {
      setLabelCache((current) => ({
        ...current,
        [nextItem.id]: nextItem.label
      }));
    }
    setSearch(nextItem?.label ?? "");
    onChange?.(nextItem?.id ?? null, nextItem);
    setOpen(false);
  }, inputValue: search, onInputValueChange: (nextValue) => {
    setSearch(nextValue);
    onSearchChange?.(nextValue);
  }, open, onOpenChange: (nextOpen) => {
    setOpen(nextOpen);
    if (nextOpen) {
      clearSelectedLabelSearch();
      return;
    }
    if (!nextOpen && selectedLabel) {
      setSearch(selectedLabel);
    }
  }, items: flatItems, filteredItems: flatItems, autoHighlight: true, disabled, readOnly, required, inputRef }, /* @__PURE__ */ React24.createElement(InputGroup, { style: {
    height: sizeStyles.height,
    borderRadius: sizeStyles.borderRadius,
    outline: error ? `1px solid ${theme.colors.red900}` : void 0
  } }, /* @__PURE__ */ React24.createElement(Input2, { id, placeholder, disabled, readOnly, onFocus: (e) => {
    setOpen(true);
    clearSelectedLabelSearch();
    onFocus?.(e);
  }, style: {
    padding: sizeStyles.padding,
    paddingRight: 0,
    height: sizeStyles.height,
    fontSize: "14px"
  }, ...restInputProps }), /* @__PURE__ */ React24.createElement(EndSlot, null, /* @__PURE__ */ React24.createElement(SpinnerSlot, { $visible: showLoading, "aria-hidden": !showLoading }, /* @__PURE__ */ React24.createElement(Spinner, { size: 14, foreground: theme.colors.gray700, background: "transparent" })), /* @__PURE__ */ React24.createElement(Trigger5, { $animateIn: animateArrowIn, $loading: showLoading, "aria-label": typeof label == "string" ? label : "Toggle options" }, /* @__PURE__ */ React24.createElement(RiArrowDownSLine2, { size: 16 })))), /* @__PURE__ */ React24.createElement(BaseCombobox.Portal, { container: dialog?.contentRef }, /* @__PURE__ */ React24.createElement(Positioner, { sideOffset: 6 }, /* @__PURE__ */ React24.createElement(Popup, null, /* @__PURE__ */ React24.createElement(List2, null, normalizedGroups.map((group, groupIndex) => group.label ? /* @__PURE__ */ React24.createElement(Group, { key: groupIndex }, /* @__PURE__ */ React24.createElement(GroupLabel, null, group.label), group.items.map((item) => /* @__PURE__ */ React24.createElement(Item6, { key: item.id, value: item.id, disabled: item.disabled }, /* @__PURE__ */ React24.createElement(ItemIndicatorSlot, null, /* @__PURE__ */ React24.createElement(ItemIndicator, null, /* @__PURE__ */ React24.createElement(RiCheckLine2, { size: 14 }))), /* @__PURE__ */ React24.createElement(ItemContent, null, renderItem ? renderItem(item) : /* @__PURE__ */ React24.createElement(React24.Fragment, null, /* @__PURE__ */ React24.createElement(ItemLabel, null, item.label), item.description && /* @__PURE__ */ React24.createElement(ItemDescription, null, item.description)))))) : group.items.map((item) => /* @__PURE__ */ React24.createElement(Item6, { key: item.id, value: item.id, disabled: item.disabled }, /* @__PURE__ */ React24.createElement(ItemIndicatorSlot, null, /* @__PURE__ */ React24.createElement(ItemIndicator, null, /* @__PURE__ */ React24.createElement(RiCheckLine2, { size: 14 }))), /* @__PURE__ */ React24.createElement(ItemContent, null, renderItem ? renderItem(item) : /* @__PURE__ */ React24.createElement(React24.Fragment, null, /* @__PURE__ */ React24.createElement(ItemLabel, null, item.label), item.description && /* @__PURE__ */ React24.createElement(ItemDescription, null, item.description)))))), !isLoading && /* @__PURE__ */ React24.createElement(EmptyState, null, empty ?? noResultsMessage)), status && /* @__PURE__ */ React24.createElement(Footer, null, /* @__PURE__ */ React24.createElement(BaseCombobox.Status, null, status)))))), error && /* @__PURE__ */ React24.createElement(Error, { size: 12, style: {
    marginTop: 6
  } }, error));
};
var ProvidedCombobox = ({
  provider,
  value,
  ...props
}) => {
  let [search, setSearch] = useState13(props.valueLabel ?? "");
  let immediateSearchQuery = search.trim() || void 0;
  let debouncedSearchQuery = useDebouncedValue(immediateSearchQuery, props.debounceMs ?? 500) || void 0;
  let searchQuery = immediateSearchQuery ? debouncedSearchQuery : void 0;
  let isSearchPending = immediateSearchQuery !== searchQuery;
  let cacheRef = useRef6(/* @__PURE__ */ new Map());
  let liveResult = provider({
    search,
    searchQuery,
    value
  });
  let resolvedQueryKey = searchQuery ?? "";
  let pendingQueryKey = immediateSearchQuery ?? "";
  let activeCacheKey = isSearchPending ? pendingQueryKey : resolvedQueryKey;
  useEffect9(() => {
    if (liveResult.isLoading) return;
    cacheRef.current.set(resolvedQueryKey, {
      items: liveResult.items,
      status: liveResult.status,
      empty: liveResult.empty
    });
  }, [liveResult.empty, liveResult.isLoading, liveResult.items, liveResult.status, resolvedQueryKey]);
  let cachedResult = cacheRef.current.get(activeCacheKey);
  let result = cachedResult && (liveResult.isLoading || isSearchPending) ? {
    items: cachedResult.items,
    status: liveResult.status ?? cachedResult.status,
    empty: liveResult.empty ?? cachedResult.empty,
    isLoading: true
  } : liveResult;
  return /* @__PURE__ */ React24.createElement(ComboboxInner, { ...props, value, search, setSearch, ...result, isLoading: result.isLoading || isSearchPending && !cachedResult, onSearchChange: props.onSearchChange });
};
var StaticCombobox = ({
  items,
  ...props
}) => {
  let [search, setSearch] = useState13(props.valueLabel ?? "");
  let filteredItems = useMemo3(() => filterGroups(items, search), [items, search]);
  return /* @__PURE__ */ React24.createElement(ComboboxInner, { ...props, items: filteredItems, search, setSearch, onSearchChange: props.onSearchChange });
};
var Combobox = (props) => {
  if ("provider" in props && props.provider) {
    return /* @__PURE__ */ React24.createElement(ProvidedCombobox, { ...props });
  }
  return /* @__PURE__ */ React24.createElement(StaticCombobox, { ...props });
};

// src/datalist/index.tsx
import React25, { Fragment } from "react";
import { styled as styled19 } from "styled-components";
var Wrapper9 = styled19("div").withConfig({
  componentId: "sc-1wdw6ce-0"
})``;
var DL = styled19("dl").withConfig({
  componentId: "sc-1wdw6ce-1"
})`display:grid;margin:0;padding:0;font-size:14px;&[data-variant='default']{gap:10px;grid-template-columns:auto 1fr;}&[data-variant='large']{gap:30px;grid-template-columns:1fr;div{gap:7px;display:grid;grid-template-columns:1fr;}}`;
var DT = styled19("dt").withConfig({
  componentId: "sc-1wdw6ce-2"
})`font-weight:600;color:${theme.colors.gray600};margin:0 !important;`;
var DD = styled19("dd").withConfig({
  componentId: "sc-1wdw6ce-3"
})`font-weight:400;margin:0 !important;`;
var Datalist = ({
  items,
  variant = "default"
}) => {
  return /* @__PURE__ */ React25.createElement(Wrapper9, null, /* @__PURE__ */ React25.createElement(DL, { "data-variant": variant }, items.map((item, i) => variant == "default" ? /* @__PURE__ */ React25.createElement(Fragment, { key: i }, /* @__PURE__ */ React25.createElement(DT, null, item.label), /* @__PURE__ */ React25.createElement(DD, null, item.value)) : /* @__PURE__ */ React25.createElement("div", { key: i }, /* @__PURE__ */ React25.createElement(DT, null, item.label), /* @__PURE__ */ React25.createElement(DD, null, item.value)))));
};

// src/date/index.tsx
import React26, { useLayoutEffect as useLayoutEffect2, useMemo as useMemo4, useReducer as useReducer2, useRef as useRef7, useState as useState14 } from "react";
import { useInterval as useInterval2 } from "react-use";
import { shleemy } from "shleemy";
import { styled as styled20 } from "styled-components";
var Header = styled20("h1").withConfig({
  componentId: "sc-vlf3ci-0"
})`font-size:14px;font-weight:600;`;
var Description3 = styled20("p").withConfig({
  componentId: "sc-vlf3ci-1"
})`font-size:13px;margin-top:5px;opacity:0.6;font-weight:500;strong{font-weight:700;}`;
var normalizeDate = (date) => {
  if (!date) return null;
  return typeof date == "string" ? new Date(date) : date;
};
var useStaticDate = (date) => {
  let [currentDate, setCurrentDate] = useState14(() => normalizeDate(date));
  let currentDateIdentifier = useRef7(!date ? "none" : String(date));
  let inputDateIdentifier = !date ? "none" : String(date);
  useLayoutEffect2(() => {
    if (currentDateIdentifier.current === inputDateIdentifier) return;
    currentDateIdentifier.current = inputDateIdentifier;
    setCurrentDate(normalizeDate(date));
  }, [inputDateIdentifier]);
  return currentDate;
};
var RenderDate = ({
  date,
  format = "full"
}) => {
  let [retriggerIndex, doRetrigger] = useReducer2((s) => s + 1, 0);
  let normalizedDate = useStaticDate(date);
  let result = useMemo4(() => {
    if (!normalizedDate) return {
      date: null,
      utc: "",
      local: "",
      pretty: "",
      timeZone: ""
    };
    let parsed = normalizedDate;
    let utc = new Date(parsed.toUTCString().replace("GMT", ""));
    let humanOffset = shleemy(parsed).forHumans;
    let pretty = format == "date" ? parsed.toLocaleDateString() : format == "time" ? parsed.toLocaleTimeString() : parsed.toLocaleString();
    return {
      date: parsed,
      humanOffset,
      utc: utc.toLocaleString(),
      local: parsed.toLocaleString(),
      pretty,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
  }, [format, normalizedDate, retriggerIndex]);
  useInterval2(() => doRetrigger(), 60 * 1e3);
  if (!result.date) return /* @__PURE__ */ React26.createElement("time", { style: {
    cursor: "pointer"
  } }, "No date");
  return /* @__PURE__ */ React26.createElement(Tooltip, { content: /* @__PURE__ */ React26.createElement("div", { style: {
    padding: 5
  } }, /* @__PURE__ */ React26.createElement(Header, null, "Time"), /* @__PURE__ */ React26.createElement(Description3, null, /* @__PURE__ */ React26.createElement("strong", null, "UTC"), ": ", /* @__PURE__ */ React26.createElement("time", null, result.utc)), /* @__PURE__ */ React26.createElement(Description3, null, /* @__PURE__ */ React26.createElement("strong", null, "Local"), ": ", /* @__PURE__ */ React26.createElement("time", null, result.local), " (", result.timeZone, ")"), /* @__PURE__ */ React26.createElement(Description3, null, /* @__PURE__ */ React26.createElement("strong", null, "Offset"), ": ", /* @__PURE__ */ React26.createElement("time", null, result.humanOffset))) }, /* @__PURE__ */ React26.createElement("span", { style: {
    display: "inline-block",
    width: "fit-content",
    height: "fit-content"
  }, onMouseEnter: doRetrigger }, /* @__PURE__ */ React26.createElement("span", { style: {
    cursor: "pointer",
    borderBottom: `1px dashed ${theme.colors.gray500}`,
    margin: 0,
    padding: 0,
    whiteSpace: "nowrap",
    lineHeight: void 0
  } }, result.pretty)));
};

// src/datePicker/index.tsx
import * as RadixPopover from "@radix-ui/react-popover";
import { VisuallyHidden as VisuallyHidden4 } from "@radix-ui/react-visually-hidden";
import { RiArrowLeftSFill, RiArrowRightSLine as RiArrowRightSLine2 } from "@remixicon/react";
import React27, { useState as useState16 } from "react";
import { keyframes as keyframes8, styled as styled21 } from "styled-components";

// src/datePicker/state.ts
import { addMonths, eachDayOfInterval, eachWeekOfInterval, endOfDay, endOfMonth, endOfWeek, isAfter, isBefore, isSameDay, isSameMonth, isToday, isWithinInterval, startOfDay, startOfMonth, startOfWeek, subMonths } from "date-fns";
import { useEffect as useEffect10, useMemo as useMemo5, useState as useState15 } from "react";
var dateToRange = (date) => {
  if (Array.isArray(date)) return date;
  return [date, date];
};
var useDatePickerState = ({
  value,
  onChange,
  type,
  open,
  maxDate,
  minDate
}) => {
  let [selectedDate, setSelectedDate] = useState15(() => value ? dateToRange(value) : void 0);
  useEffect10(() => {
    setSelectedDate(value ? dateToRange(value) : void 0);
  }, [value, open]);
  let [currentMonth, setCurrentMonth] = useState15(() => startOfMonth(selectedDate ? selectedDate[0] : /* @__PURE__ */ new Date()));
  useEffect10(() => {
    let start = startOfWeek(startOfMonth(selectedDate ? selectedDate[0] : /* @__PURE__ */ new Date()));
    let end = endOfWeek(endOfMonth(selectedDate ? selectedDate[1] : /* @__PURE__ */ new Date()));
    if (!isWithinInterval(currentMonth, {
      start,
      end
    })) {
      setCurrentMonth(startOfMonth(selectedDate ? selectedDate[0] : /* @__PURE__ */ new Date()));
    }
  }, [selectedDate]);
  let prevMonthEnabled = !minDate || isAfter(currentMonth, minDate);
  let nextMonthEnabled = !maxDate || isBefore(addMonths(currentMonth, 1), maxDate);
  let nextMonth = () => setCurrentMonth(startOfMonth(addMonths(currentMonth, 1)));
  let prevMonth = () => setCurrentMonth(startOfMonth(subMonths(currentMonth, 1)));
  let days = useMemo5(() => {
    let startDate = currentMonth;
    let endDate = endOfMonth(currentMonth);
    let weeks = eachWeekOfInterval({
      start: startDate,
      end: endDate
    });
    return weeks.map((week) => {
      return {
        id: `week_${week.getTime()}`,
        days: eachDayOfInterval({
          start: startOfWeek(week),
          end: endOfWeek(week)
        }).map((day) => {
          let isInSelectedRange = !!(selectedDate && isWithinInterval(day, {
            start: selectedDate[0],
            end: selectedDate[1]
          }));
          let isStartOfRange = !!selectedDate && isSameDay(day, selectedDate[0]);
          let isEndOfRange = !!selectedDate && isSameDay(day, selectedDate[1]);
          return {
            id: `day_${day.getTime()}`,
            date: day,
            isToday: isToday(day),
            isInSelectedRange,
            isSelected: isStartOfRange || isEndOfRange,
            isStartOfRange,
            isEndOfRange,
            isCurrentMonth: isSameMonth(day, currentMonth)
          };
        })
      };
    });
  }, [currentMonth, selectedDate]);
  let selectDate = (date) => {
    if (type == "single") {
      let range = dateToRange(date);
      setSelectedDate(range);
      onChange(range[0]);
    } else {
      let range;
      if (Array.isArray(date)) {
        range = date;
      } else {
        if (!selectedDate || isSameDay(date, selectedDate[0]) || isSameDay(date, selectedDate[1])) {
          range = [date, date];
        } else if (isBefore(date, selectedDate[0])) {
          range = [date, selectedDate[1]];
        } else if (isAfter(date, selectedDate[1])) {
          range = [selectedDate[0], date];
        } else {
          range = [selectedDate[0], date];
        }
      }
      let normalizedRange = [startOfDay(range[0]), endOfDay(range[1])];
      setSelectedDate(normalizedRange);
      onChange(normalizedRange);
    }
  };
  let stringValue = useMemo5(() => {
    if (!selectedDate) return void 0;
    if (type == "single") {
      return selectedDate[0].toLocaleDateString();
    } else {
      return selectedDate.map((d) => d.toLocaleDateString()).join(" - ");
    }
  }, [selectedDate, type]);
  let reset = () => {
    setSelectedDate(void 0);
    onChange(void 0);
  };
  return {
    currentMonth,
    nextMonth,
    prevMonth,
    days,
    selectDate,
    stringValue,
    reset,
    nextMonthEnabled,
    prevMonthEnabled
  };
};

// src/datePicker/index.tsx
var fadeIn4 = keyframes8`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
`;
var fadeOut3 = keyframes8`
  from {
    opacity: 1;
    transform: scale(1);
  }

  to {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
`;
var Trigger7 = styled21(RadixPopover.Trigger).attrs({
  type: "button"
}).withConfig({
  componentId: "sc-1wby6r1-0"
})`display:flex;outline:1px solid transparent;background:${theme.colors.gray300};color:${theme.colors.foreground};outline:none;width:100%;gap:10px;align-items:center;justify-content:space-between;transition:all 0.3s ease;border:none;font-weight:500;&:focus-within,&:focus{background:${theme.colors.gray300};outline:1px solid ${theme.colors.gray600};}& [data-placeholder]{font-weight:500;}&:disabled{cursor:not-allowed;opacity:0.7;}`;
var Content11 = styled21(RadixPopover.Content).withConfig({
  componentId: "sc-1wby6r1-1"
})`background:${theme.colors.gray100};border:1px solid ${theme.colors.gray400};border-radius:10px;box-shadow:${theme.shadows.medium};overflow:hidden;display:flex;flex-direction:column;pointer-events:auto;&[data-state='open']{animation:${fadeIn4} 0.15s ease-in-out;}&[data-state='closed']{animation:${fadeOut3} 0.15s ease-in-out;}`;
var Header2 = styled21("header").withConfig({
  componentId: "sc-1wby6r1-2"
})`display:flex;justify-content:space-between;padding:10px;align-items:center;border-bottom:1px solid ${theme.colors.gray300};`;
var Month = styled21("div").withConfig({
  componentId: "sc-1wby6r1-3"
})`display:flex;flex-direction:column;padding:10px;gap:2px;`;
var Week = styled21("div").withConfig({
  componentId: "sc-1wby6r1-4"
})`display:grid;grid-template-columns:repeat(7,1fr);gap:2px;`;
var Day = styled21("button").attrs({
  type: "button"
}).withConfig({
  componentId: "sc-1wby6r1-5"
})`display:flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:5px;cursor:pointer;background:transparent;color:${theme.colors.gray800};border:none;transition:all 0.3s ease;font-weight:500;font-size:14px;&[data-today='true']{background:${theme.colors.blue100};color:${theme.colors.blue700};}&[data-in-range='true']{background:${theme.colors.blue100};color:${theme.colors.blue800};}&[data-selected='true']{background:${theme.colors.blue400};color:${theme.colors.blue900};}&[data-not-in-month='true']{opacity:0.5;}&:disabled{cursor:not-allowed;opacity:0.5;}`;
var Footer2 = styled21("footer").withConfig({
  componentId: "sc-1wby6r1-6"
})`display:flex;justify-content:flex-end;padding:0px 10px 10px 10px;gap:10px;`;
var WeekDay = styled21("div").withConfig({
  componentId: "sc-1wby6r1-7"
})`text-align:center;font-size:12px;padding-bottom:5px;color:${theme.colors.gray600};font-weight:500;`;
var DatePicker = (props) => {
  let state = useDatePickerState(props);
  let [open, setOpen] = useState16(false);
  let sizeStyles = getButtonSize(props.size ?? "3");
  let zIndex = useDialogZIndex(open);
  return /* @__PURE__ */ React27.createElement("div", null, /* @__PURE__ */ React27.createElement(RadixPopover.Root, { open, onOpenChange: setOpen }, /* @__PURE__ */ React27.createElement("div", { style: {
    display: "flex",
    flexDirection: "column"
  } }, props.hideLabel ? /* @__PURE__ */ React27.createElement(VisuallyHidden4, null, /* @__PURE__ */ React27.createElement("label", null, props.label)) : /* @__PURE__ */ React27.createElement(InputLabel, null, props.label), props.description && /* @__PURE__ */ React27.createElement(InputDescription, null, props.description), /* @__PURE__ */ React27.createElement(Trigger7, { style: {
    ...sizeStyles,
    fontSize: 14
  }, disabled: props.disabled }, state.stringValue || /* @__PURE__ */ React27.createElement("span", { "data-placeholder": true }, props.placeholder))), /* @__PURE__ */ React27.createElement(RadixPopover.Portal, null, /* @__PURE__ */ React27.createElement(Content11, { sideOffset: 5, side: "bottom", style: {
    zIndex
  } }, /* @__PURE__ */ React27.createElement(Header2, null, /* @__PURE__ */ React27.createElement(Tooltip, { content: "Previous Month" }, /* @__PURE__ */ React27.createElement(Button, { type: "button", size: "2", variant: "soft", iconLeft: /* @__PURE__ */ React27.createElement(RiArrowLeftSFill, null), onClick: state.prevMonth, disabled: !state.prevMonthEnabled })), /* @__PURE__ */ React27.createElement(AnimateContent, { identifier: state.currentMonth.getTime() }, /* @__PURE__ */ React27.createElement(Title3, { as: "h1", weight: "bold", size: "2", align: "center" }, state.currentMonth.toLocaleDateString("en", {
    month: "long",
    year: "numeric"
  }))), /* @__PURE__ */ React27.createElement(Tooltip, { content: "Next Month" }, /* @__PURE__ */ React27.createElement(Button, { type: "button", size: "2", variant: "soft", iconLeft: /* @__PURE__ */ React27.createElement(RiArrowRightSLine2, null), onClick: state.nextMonth, disabled: !state.nextMonthEnabled }))), /* @__PURE__ */ React27.createElement(AnimatePanes, { orderedIdentifier: state.currentMonth.getTime() }, /* @__PURE__ */ React27.createElement(Month, null, /* @__PURE__ */ React27.createElement(Week, null, state.days[0].days.map((day) => /* @__PURE__ */ React27.createElement(WeekDay, { key: day.id }, day.date.toLocaleDateString("en", {
    weekday: "short"
  })))), state.days.map((week) => /* @__PURE__ */ React27.createElement(Week, { key: week.id }, week.days.map((day) => /* @__PURE__ */ React27.createElement(Day, { key: day.id, "data-today": day.isToday, "data-in-range": day.isInSelectedRange, "data-selected": day.isSelected, "data-not-in-month": !day.isCurrentMonth, onClick: () => state.selectDate(day.date), disabled: props.maxDate && day.date > props.maxDate || props.minDate && day.date < props.minDate }, day.date.getDate())))))), /* @__PURE__ */ React27.createElement(Footer2, null, props.resettable && /* @__PURE__ */ React27.createElement(Button, { type: "button", size: "2", variant: "soft", onClick: () => {
    state.reset();
  } }, "Reset"), /* @__PURE__ */ React27.createElement(RadixPopover.Close, { asChild: true }, /* @__PURE__ */ React27.createElement(Button, { type: "button", size: "2", variant: "soft", autoFocus: true, onClick: () => {
    setOpen(false);
  } }, "Done")))))));
};

// src/dialog/dialog.tsx
import * as RadixDialogDialog2 from "@radix-ui/react-dialog";
import { RiCloseLine } from "@remixicon/react";
import React28, { useRef as useRef8 } from "react";
import { styled as styled23 } from "styled-components";

// src/dialog/styles.ts
import * as RadixDialogDialog from "@radix-ui/react-dialog";
import { keyframes as keyframes9, styled as styled22 } from "styled-components";
var fadeInShift2 = keyframes9`
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;
var fadeOutShift2 = keyframes9`
  from {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
  to {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }
`;
var fadeIn5 = keyframes9`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
var fadeOut4 = keyframes9`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;
var fadeInRight2 = keyframes9`
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;
var fadeOutRight2 = keyframes9`
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(50px);
  }
`;
var Overlay5 = styled22(RadixDialogDialog.Overlay).withConfig({
  componentId: "sc-1xhkfuh-0"
})`position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(100,100,100,0.1);backdrop-filter:blur(5px);&[data-state='open']{animation:${fadeIn5} 200ms ease-out forwards;}&[data-state='closed']{animation:${fadeOut4} 200ms ease-in forwards;}`;
var Content13 = styled22(RadixDialogDialog.Content).withConfig({
  componentId: "sc-1xhkfuh-1"
})`position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:${theme.colors.background};border-radius:10px;box-shadow:${theme.shadows.large};color:${theme.colors.gray900};padding:20px;&[data-state='open']{animation:${fadeInShift2} 200ms ease-out;}&[data-state='closed']{animation:${fadeOutShift2} 200ms ease-in;}`;
var ContentSide = styled22(RadixDialogDialog.Content).withConfig({
  componentId: "sc-1xhkfuh-2"
})`position:fixed;top:10px;right:10px;bottom:10px;background:${theme.colors.background};box-shadow:${theme.shadows.large};height:calc(100% - 20px);width:min(600px,90vw);border-radius:10px;display:flex;flex-direction:column;overflow:hidden;max-width:calc(100% - 20px);--dialog-radius:10px;&[data-state='open']{animation:${fadeInRight2} 200ms ease-out;}&[data-state='closed']{animation:${fadeOutRight2} 200ms ease-in;}`;
var Close2 = styled22("button").attrs({
  type: "button"
}).withConfig({
  componentId: "sc-1xhkfuh-3"
})`position:absolute;top:15px;right:15px;height:26px;width:26px;display:flex;align-items:center;justify-content:center;padding:0;border-radius:50%;border:none;transition:background 0.3s ease;background:${theme.colors.gray400};&:hover{background:${theme.colors.gray500};}`;

// src/dialog/dialog.tsx
var Wrapper10 = styled23(Content13).withConfig({
  componentId: "sc-1big1xm-0"
})`width:90vw;max-height:90vh;overflow:auto;background:${theme.colors.background};box-shadow:0 0 10px rgba(0,0,0,0.1);padding:0px;border-radius:15px;border:solid 1px ${theme.colors.gray500};transition:all 0.2s ease-in-out;`;
var Inner3 = styled23("div").withConfig({
  componentId: "sc-1big1xm-1"
})`color:${theme.colors.gray900};${({
  $variant
}) => $variant === "padded" ? `
    padding: 120px 60px;

    @media (max-width: 800px) {
      padding: 20px;
    }
  ` : `
    padding: 20px;
  `}`;
var Title6 = styled23(RadixDialogDialog2.Title).withConfig({
  componentId: "sc-1big1xm-2"
})`padding:0;font-size:20px;font-weight:600;padding:0px 0px 6px 0px;`;
var Description5 = styled23(RadixDialogDialog2.Description).withConfig({
  componentId: "sc-1big1xm-3"
})`padding:0px 0px 20px 0px;font-size:14px;font-weight:500;color:${theme.colors.gray600};`;
var Actions2 = styled23("div").withConfig({
  componentId: "sc-1big1xm-4"
})`display:flex;justify-content:flex-end;gap:10px;padding:0;`;
var Dialog = {
  Wrapper: ({
    children,
    isOpen,
    onOpenChange,
    style,
    autoCloseOnSubmit,
    variant = "slim",
    width
  }) => {
    let zIndex = useDialogZIndex(isOpen);
    let contentRef = useRef8(null);
    return /* @__PURE__ */ React28.createElement(DialogProvider, { value: {
      isOpen,
      onOpenChange,
      autoCloseOnSubmit,
      contentRef
    } }, /* @__PURE__ */ React28.createElement(RadixDialogDialog2.Root, { open: isOpen, onOpenChange }, /* @__PURE__ */ React28.createElement(RadixDialogDialog2.Portal, null, /* @__PURE__ */ React28.createElement(Overlay5, { style: {
      zIndex
    } }), /* @__PURE__ */ React28.createElement(Wrapper10, { ref: contentRef, style: {
      zIndex: zIndex + 1,
      maxWidth: width ? width : variant == "padded" ? 480 : 400,
      ...style
    } }, /* @__PURE__ */ React28.createElement(Inner3, { $variant: variant }, children, /* @__PURE__ */ React28.createElement(RadixDialogDialog2.Close, { asChild: true }, /* @__PURE__ */ React28.createElement(Close2, { "aria-label": "Close" }, /* @__PURE__ */ React28.createElement(RiCloseLine, { size: 16 }))))))));
  },
  Title: ({
    children
  }) => {
    return /* @__PURE__ */ React28.createElement(Title6, null, children);
  },
  Description: ({
    children
  }) => {
    return /* @__PURE__ */ React28.createElement(Description5, null, children);
  },
  Actions: ({
    children
  }) => {
    return /* @__PURE__ */ React28.createElement(Actions2, null, children);
  }
};

// src/dialog/largePanel.tsx
import * as RadixDialogDialog3 from "@radix-ui/react-dialog";
import { RiCloseLine as RiCloseLine2 } from "@remixicon/react";
import React29, { useRef as useRef9 } from "react";
import { styled as styled24 } from "styled-components";
var PanelContent = styled24(Content13).withConfig({
  componentId: "sc-1y70n6v-0"
})`max-width:90vw;width:min(max(400px,80vw),1100px);min-height:min(70vh,850px);max-height:90vh;padding:0;overflow:auto;`;
var LargePanelDialog = {
  Wrapper: ({
    children,
    isOpen,
    onOpenChange,
    style,
    autoCloseOnSubmit,
    closeButton = true
  }) => {
    let zIndex = useDialogZIndex(isOpen);
    let contentRef = useRef9(null);
    return /* @__PURE__ */ React29.createElement(DialogProvider, { value: {
      isOpen,
      onOpenChange,
      autoCloseOnSubmit,
      contentRef
    } }, /* @__PURE__ */ React29.createElement(RadixDialogDialog3.Root, { open: isOpen, onOpenChange }, /* @__PURE__ */ React29.createElement(RadixDialogDialog3.Portal, null, /* @__PURE__ */ React29.createElement(Overlay5, { style: {
      zIndex
    } }), /* @__PURE__ */ React29.createElement(PanelContent, { ref: contentRef, style: {
      ...style,
      zIndex: zIndex + 1
    } }, children, closeButton && /* @__PURE__ */ React29.createElement(RadixDialogDialog3.Close, { asChild: true }, /* @__PURE__ */ React29.createElement(Close2, { "aria-label": "Close" }, /* @__PURE__ */ React29.createElement(RiCloseLine2, { size: 16 })))))));
  }
};

// src/dialog/panel.tsx
import * as RadixDialogDialog4 from "@radix-ui/react-dialog";
import { RiCloseLine as RiCloseLine3 } from "@remixicon/react";
import React30, { useRef as useRef10 } from "react";
import { styled as styled25 } from "styled-components";
var Wrapper11 = styled25(ContentSide).withConfig({
  componentId: "sc-4b717a-0"
})`transition:width 0.2s ease;overflow-y:auto;overflow-x:hidden;overscroll-behavior:contain;`;
var Header3 = styled25("header").withConfig({
  componentId: "sc-4b717a-1"
})`display:flex;padding:15px 20px;border-bottom:1px solid ${theme.colors.gray400};background:rgba(255,255,255,0.7);z-index:9999;backdrop-filter:blur(10px);justify-content:space-between;align-items:center;position:sticky;top:0;border-top-right-radius:var(--dialog-radius);border-top-left-radius:var(--dialog-radius);`;
var Content14 = styled25("main").withConfig({
  componentId: "sc-4b717a-2"
})`display:flex;flex-direction:column;flex:1 0 auto;padding:15px 20px;`;
var Title8 = styled25(RadixDialogDialog4.Title).withConfig({
  componentId: "sc-4b717a-3"
})`padding:0;font-size:20px;font-weight:600;margin-bottom:4px;`;
var Description7 = styled25(RadixDialogDialog4.Description).withConfig({
  componentId: "sc-4b717a-4"
})`font-size:14px;font-weight:500;color:${theme.colors.gray600};`;
var Actions3 = styled25("div").withConfig({
  componentId: "sc-4b717a-5"
})`display:flex;justify-content:flex-end;gap:10px;padding:0;`;
var Panel = {
  Wrapper: ({
    children,
    isOpen,
    onOpenChange,
    style,
    autoCloseOnSubmit,
    width
  }) => {
    let zIndex = useDialogZIndex(isOpen);
    let contentRef = useRef10(null);
    return /* @__PURE__ */ React30.createElement(DialogProvider, { value: {
      isOpen,
      onOpenChange,
      autoCloseOnSubmit,
      contentRef
    } }, /* @__PURE__ */ React30.createElement(RadixDialogDialog4.Root, { open: isOpen, onOpenChange }, /* @__PURE__ */ React30.createElement(RadixDialogDialog4.Portal, null, /* @__PURE__ */ React30.createElement(Overlay5, { style: {
      zIndex
    } }), /* @__PURE__ */ React30.createElement(Wrapper11, { ref: contentRef, style: {
      ...style,
      width,
      zIndex: zIndex + 1
    } }, children))));
  },
  Title: ({
    children
  }) => {
    return /* @__PURE__ */ React30.createElement(Title8, null, children);
  },
  Description: ({
    children
  }) => {
    return /* @__PURE__ */ React30.createElement(Description7, null, children);
  },
  Actions: ({
    children
  }) => {
    return /* @__PURE__ */ React30.createElement(Actions3, null, children);
  },
  Content: ({
    children
  }) => {
    return /* @__PURE__ */ React30.createElement(Content14, null, children);
  },
  Header: ({
    children
  }) => {
    return /* @__PURE__ */ React30.createElement(Header3, null, /* @__PURE__ */ React30.createElement("div", null, children), /* @__PURE__ */ React30.createElement(RadixDialogDialog4.Close, { asChild: true }, /* @__PURE__ */ React30.createElement(Button, { type: "button", "aria-label": "Close", iconLeft: /* @__PURE__ */ React30.createElement(RiCloseLine3, null) })));
  }
};

// src/entity/content.tsx
import React31 from "react";
import { styled as styled26 } from "styled-components";

// src/entity/context.ts
import { createContext, useContext as useContext2 } from "react";
var EntityContext = createContext({
  aligned: false
});
var useEntityContext = () => useContext2(EntityContext);

// src/entity/content.tsx
var Wrapper12 = styled26("main").withConfig({
  componentId: "sc-d0rsg6-0"
})`display:flex;flex-direction:row;flex-wrap:wrap;flex:1;justify-content:flex-start;align-items:stretch;padding:0px 20px;@media (max-width:600px){padding:0px;flex-direction:column;flex-wrap:nowrap;}&:not(:first-child){border-top:solid 1px ${theme.colors.gray300};}`;
var ContentEntityWrapper = styled26("div").withConfig({
  shouldForwardProp: (prop) => prop !== "$aligned"
}).withConfig({
  componentId: "sc-d0rsg6-1"
})`display:flex;${({
  $aligned
}) => $aligned ? `
        flex: 1 1 0;
        min-width: 0;
      ` : `
        flex-grow: 1;
      `}`;
var MobileBorderStyle = styled26(ContentEntityWrapper).withConfig({
  componentId: "sc-d0rsg6-2"
})`@media (max-width:600px){border-bottom:1px solid ${theme.colors.gray300};}`;
var ContentEntity = ({
  children,
  last,
  aligned
}) => {
  let Element2 = last ? ContentEntityWrapper : MobileBorderStyle;
  return /* @__PURE__ */ React31.createElement(Element2, { $aligned: aligned }, children);
};
var EntityContent = ({
  children,
  aligned
}) => {
  let context = useEntityContext();
  let isAligned = aligned ?? context.aligned;
  let childArray = Array.isArray(children) ? children : [children];
  return /* @__PURE__ */ React31.createElement(Wrapper12, null, childArray.map((child, i) => /* @__PURE__ */ React31.createElement(ContentEntity, { key: i, last: i == childArray.length - 1, aligned: isAligned }, child)));
};

// src/entity/contentRaw.tsx
import { styled as styled27 } from "styled-components";
var EntityContentRaw = styled27("section").withConfig({
  componentId: "sc-otha5r-0"
})`padding:10px 20px;display:flex;flex-direction:column;flex-wrap:wrap;flex:1;&:not(:first-child){border-top:solid 1px ${theme.colors.gray300};}`;

// src/entity/field.tsx
import React33 from "react";
import { styled as styled28 } from "styled-components";

// src/spacer/index.tsx
import React32 from "react";
var Spacer = ({
  size,
  height,
  width,
  grow,
  shrink = false
}) => {
  return /* @__PURE__ */ React32.createElement("div", { style: {
    height: height ?? size,
    width: width ?? size,
    flexGrow: !height && !width && !size || grow ? 1 : void 0,
    flexShrink: shrink === false ? 0 : shrink ? 1 : void 0
  } });
};

// src/entity/field.tsx
var Wrapper13 = styled28("div").withConfig({
  componentId: "sc-13k0pcr-0"
})`display:flex;padding:20px 0px;align-items:center;@media (max-width:600px){padding:20px;}`;
var Content15 = styled28("div").withConfig({
  componentId: "sc-13k0pcr-1"
})`display:flex;flex-direction:column;`;
var Title9 = styled28("h1").withConfig({
  componentId: "sc-13k0pcr-2"
})`font-size:14px;font-weight:600;line-height:1;`;
var Description8 = styled28("p").withConfig({
  componentId: "sc-13k0pcr-3"
})`font-size:12px;font-weight:400;color:${theme.colors.gray700};margin-top:4px;line-height:1;`;
var EntityField = ({
  title,
  description,
  value,
  width,
  children,
  position,
  style,
  right,
  prefix,
  suffix,
  prefixNoSpace,
  suffixNoSpace,
  skeleton
}) => {
  if (!position) position = "grow";
  if (!description) description = value;
  return /* @__PURE__ */ React33.createElement(Wrapper13, { style: {
    width,
    flex: position == "grow" ? 1 : void 0,
    textAlign: right ? "right" : void 0,
    ...style
  } }, right && /* @__PURE__ */ React33.createElement(Spacer, null), prefix && /* @__PURE__ */ React33.createElement("div", { style: {
    marginRight: prefixNoSpace ? 0 : 10
  } }, prefix), /* @__PURE__ */ React33.createElement(Content15, null, children ? children : /* @__PURE__ */ React33.createElement(React33.Fragment, null, /* @__PURE__ */ React33.createElement(Title9, null, title), description && /* @__PURE__ */ React33.createElement(React33.Fragment, null, /* @__PURE__ */ React33.createElement(Spacer, { height: 3 }), /* @__PURE__ */ React33.createElement(Description8, null, description)))), suffix && /* @__PURE__ */ React33.createElement("div", { style: {
    marginLeft: suffixNoSpace ? 0 : 10
  } }, suffix));
};

// src/entity/footer.tsx
import React34 from "react";
import { styled as styled29 } from "styled-components";
var Footer3 = styled29("footer").withConfig({
  componentId: "sc-16o8pum-0"
})`padding:10px 20px;display:flex;flex-direction:row;flex-wrap:wrap;flex:1;justify-content:flex-start;align-items:stretch;font-size:16px;font-weight:600;&:not(:first-child){border-top:solid 1px ${theme.colors.gray300};}`;
var EntityFooter = ({
  children
}) => {
  return /* @__PURE__ */ React34.createElement(Footer3, null, children);
};

// src/entity/header.tsx
import React35 from "react";
import { styled as styled30 } from "styled-components";
var Header4 = styled30("header").withConfig({
  componentId: "sc-rim8id-0"
})`padding:10px 20px;display:flex;flex-direction:row;flex-wrap:wrap;flex:1;justify-content:flex-start;align-items:stretch;font-size:16px;font-weight:600;&:not(:first-child){border-top:solid 1px ${theme.colors.gray300};}`;
var EntityHeader = ({
  children
}) => {
  return /* @__PURE__ */ React35.createElement(Header4, null, children);
};

// src/entity/wrapper.tsx
import React36, { useMemo as useMemo6 } from "react";
import { styled as styled31 } from "styled-components";
var Wrapper14 = styled31("div").withConfig({
  componentId: "sc-15ldyue-0"
})`border-radius:12px;border:1px solid ${theme.colors.gray300};display:flex;flex-direction:column;position:relative;color:${theme.colors.foreground};`;
var EntityWrapper = ({
  children,
  style,
  aligned,
  skeleton
}) => {
  let contextValue = useMemo6(() => ({
    aligned: !!aligned
  }), [aligned]);
  return /* @__PURE__ */ React36.createElement(EntityContext.Provider, { value: contextValue }, /* @__PURE__ */ React36.createElement(Wrapper14, { style }, children));
};

// src/entity/index.ts
var Entity = {
  Wrapper: EntityWrapper,
  Footer: EntityFooter,
  Field: EntityField,
  Content: EntityContent,
  Header: EntityHeader,
  ContentRaw: EntityContentRaw
};

// src/flex/index.tsx
import React37 from "react";
var Flex = ({
  children,
  direction = "row",
  align,
  justify,
  wrap = "nowrap",
  gap = 0,
  ...props
}) => {
  return /* @__PURE__ */ React37.createElement("div", { ...props, style: {
    display: "flex",
    flexDirection: direction,
    alignItems: align,
    justifyContent: justify,
    flexWrap: wrap,
    gap,
    ...props.style
  } }, children);
};

// src/group/index.tsx
import React38, { useContext as useContext3 } from "react";
import { styled as styled32 } from "styled-components";
var GroupWrapper = styled32("div").withConfig({
  componentId: "sc-h0wdxo-0"
})`border:solid 1px ${theme.colors.gray300};border-radius:10px;overflow:hidden;& > *:not(:last-child){border-bottom:solid 1px ${theme.colors.gray300};}`;
var IsGroupContext = React38.createContext(false);
var useIsGroup = () => useContext3(IsGroupContext);
var Group2 = {
  Wrapper: (props) => /* @__PURE__ */ React38.createElement(IsGroupContext.Provider, { value: true }, /* @__PURE__ */ React38.createElement(GroupWrapper, null, props.children)),
  Header: (props) => /* @__PURE__ */ React38.createElement(Group2.HeaderRow, null, /* @__PURE__ */ React38.createElement("div", { style: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6
  } }, /* @__PURE__ */ React38.createElement("div", { style: {
    display: "flex",
    flexDirection: "column",
    gap: 2
  } }, /* @__PURE__ */ React38.createElement(Title3, { as: "h2", size: "3", weight: "strong" }, props.title), props.description && /* @__PURE__ */ React38.createElement(Text, { size: "2", weight: "medium", color: "gray600" }, props.description)), /* @__PURE__ */ React38.createElement("div", { style: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 10
  } }, props.actions))),
  HeaderRow: styled32("header").withConfig({
    componentId: "sc-h0wdxo-1"
  })`padding:15px 20px;display:flex;flex-direction:column;&:not(:last-child){border-bottom:solid 1px ${theme.colors.gray300};}`,
  Row: styled32("main").withConfig({
    componentId: "sc-h0wdxo-2"
  })`display:flex;flex-direction:column;`,
  Content: styled32("div").withConfig({
    componentId: "sc-h0wdxo-3"
  })`padding:15px;display:flex;flex-direction:column;`,
  Footer: styled32("footer").withConfig({
    componentId: "sc-h0wdxo-4"
  })`padding:10px 15px;display:flex;align-items:center;`
};

// src/info/index.tsx
import { RiInformation2Line } from "@remixicon/react";
import React39 from "react";
import { styled as styled33 } from "styled-components";
var Button2 = styled33("button").attrs({
  type: "button"
}).withConfig({
  componentId: "sc-wuucny-0"
})`background:none;border:none;padding:3px;display:flex;align-items:center;justify-content:center;color:${theme.colors.gray600};& > div{border-radius:5px;display:flex;align-items:center;justify-content:center;svg{height:70%;width:70%;}}&:hover,&:focus{& > div{background:${theme.colors.gray300};color:${theme.colors.foreground};}}`;
var InfoTooltip = (props) => {
  return /* @__PURE__ */ React39.createElement(Tooltip, { content: props.children }, /* @__PURE__ */ React39.createElement(Button2, null, /* @__PURE__ */ React39.createElement("div", { style: {
    height: props.size ?? 20,
    width: props.size ?? 20
  } }, /* @__PURE__ */ React39.createElement(RiInformation2Line, null))));
};

// src/inlineCopy/index.tsx
import { RiCheckLine as RiCheckLine3, RiFileCopy2Line } from "@remixicon/react";
import copy2 from "copy-to-clipboard";
import React40, { useRef as useRef11, useState as useState17 } from "react";
import { toast as toast2 } from "sonner";
import { styled as styled34 } from "styled-components";
var Button3 = styled34("button").withConfig({
  componentId: "sc-2zba5c-0"
})`background:none;border:none;padding:3px;display:flex;align-items:center;justify-content:center;color:${theme.colors.gray600};& > div{border-radius:5px;display:flex;align-items:center;justify-content:center;svg{height:70%;width:70%;}}&:hover,&:focus{& > div{background:${theme.colors.gray300};color:${theme.colors.foreground};}}`;
var InlineCopy = (props) => {
  let [copied, setCopied] = useState17(false);
  let copiedToRef = useRef11(null);
  if (!props.value) return null;
  return /* @__PURE__ */ React40.createElement(Tooltip, { content: props.tooltip ?? "Copy" }, /* @__PURE__ */ React40.createElement(Button3, { type: "button", onClick: (e) => {
    e.stopPropagation();
    e.preventDefault();
    copy2(props.value);
    toast2.success("Copied to clipboard");
    clearTimeout(copiedToRef.current);
    copiedToRef.current = setTimeout(() => {
      setCopied(false);
    }, 2e3);
  } }, /* @__PURE__ */ React40.createElement("div", { style: {
    height: props.size ?? 20,
    width: props.size ?? 20
  } }, copied ? /* @__PURE__ */ React40.createElement(RiCheckLine3, null) : /* @__PURE__ */ React40.createElement(RiFileCopy2Line, null))));
};

// src/linkTabs/index.tsx
import React42, { useMemo as useMemo7 } from "react";

// src/tabs/index.tsx
import { useIsSSR } from "@looped/hooks";
import React41, { useState as useState18 } from "react";
import { useWindowSize } from "react-use";
import { styled as styled35 } from "styled-components";
var Wrapper15 = styled35("div").withConfig({
  componentId: "sc-j2kvo3-0"
})`position:relative;width:100%;user-select:none;`;
var Inner4 = styled35("div").withConfig({
  componentId: "sc-j2kvo3-1"
})`display:flex;padding:0;margin:0;border-bottom:1px solid ${theme.colors.gray300};overflow-x:auto;white-space:nowrap;justify-content:center;scrollbar-color:${theme.colors.gray400} transparent;scrollbar-width:thin;`;
var List3 = styled35("ul").withConfig({
  componentId: "sc-j2kvo3-2"
})`list-style:none;display:flex;max-width:100%;overflow-x:auto;white-space:nowrap;scrollbar-color:${theme.colors.gray400} transparent;scrollbar-width:thin;`;
var Tab = styled35("li").withConfig({
  componentId: "sc-j2kvo3-3"
})`position:relative;z-index:10;button,a{font-size:14px;font-weight:500;color:${theme.colors.gray700};text-decoration:none;display:flex;align-items:center;justify-content:center;transition:all 0.3s ease;background:none;border:none;padding:0;&:hover{color:${theme.colors.blue900};}}`;
var LineIndicator = styled35("div").withConfig({
  componentId: "sc-j2kvo3-4"
})`position:absolute;bottom:-1px;height:3px;background:${theme.colors.blue900};transition:all 0.3s ease;border-radius:7px;`;
var TabIndicator = styled35("div").withConfig({
  componentId: "sc-j2kvo3-5"
})`position:absolute;transform:translateY(-50%);transition:all 0.3s ease;border-radius:7px;z-index:0;`;
var Tabs = ({
  current,
  tabs,
  action,
  tabIndicator = true,
  lineIndicator = true,
  height = 30,
  gap = 20,
  padding: paddingRaw = {},
  margin = {
    bottom: 20,
    top: 0
  },
  variant = "soft",
  maxWidth = "100%"
}) => {
  let Link = getLink();
  let isServer = useIsSSR();
  useWindowSize();
  let padding = {
    left: paddingRaw.left ?? 0,
    right: paddingRaw.right ?? 0,
    top: paddingRaw.top ?? 0,
    bottom: paddingRaw.bottom ?? 6
  };
  let [listRef, setListRef] = useState18(null);
  let [innerRef, setInnerRef] = useState18(null);
  let [wrapperRef, setWrapperRef] = useState18(null);
  let [scrollOffset, setScrollOffset] = useState18(0);
  let [refs, setRefs] = useState18([]);
  let renderIndicators = false;
  let currentIndex = tabs.findIndex((t) => "to" in t ? t.to == current : t.id == current);
  let indicatorOffset = -scrollOffset;
  let indicatorWidth = refs[currentIndex]?.clientWidth ?? 0;
  if (refs[currentIndex] && wrapperRef) {
    renderIndicators = true;
    let tabRect = refs[currentIndex].getBoundingClientRect();
    let wrapperRect = wrapperRef.getBoundingClientRect();
    indicatorOffset = tabRect.left - wrapperRect.left;
    indicatorWidth = tabRect.width;
  }
  let clipIndicator = (indicator) => {
    let overflowThreshold = 10;
    let minLeft = -overflowThreshold;
    let maxRight = (wrapperRef?.clientWidth ?? 0) + overflowThreshold;
    let right = indicator.left + indicator.width;
    let clippedLeft = Math.max(indicator.left, minLeft);
    let clippedRight = Math.min(right, maxRight);
    return {
      left: clippedLeft,
      width: Math.max(clippedRight - clippedLeft, 0),
      visible: clippedRight - clippedLeft >= 30
    };
  };
  let clippedLineIndicator = clipIndicator({
    left: indicatorOffset,
    width: indicatorWidth
  });
  let clippedTabIndicator = clipIndicator({
    left: indicatorOffset - gap / 2,
    width: indicatorWidth + gap
  });
  return /* @__PURE__ */ React41.createElement(Wrapper15, { ref: setWrapperRef }, /* @__PURE__ */ React41.createElement(Inner4, { ref: setInnerRef, onScroll: (e) => setScrollOffset(e.currentTarget.scrollLeft + (listRef?.scrollLeft ?? 0)), style: {
    width: "100%",
    paddingLeft: padding.left,
    paddingRight: padding.right,
    paddingTop: padding.top,
    paddingBottom: padding.bottom,
    marginBottom: margin.bottom,
    marginTop: margin.top
  } }, /* @__PURE__ */ React41.createElement(List3, { ref: setListRef, onScroll: (e) => setScrollOffset((innerRef?.scrollLeft ?? 0) + e.currentTarget.scrollLeft), style: {
    width: maxWidth,
    gap
  } }, tabs.map((item, i) => /* @__PURE__ */ React41.createElement(Tab, { key: i, ref: (el) => {
    setRefs((refs2) => {
      refs2[i] = el;
      return refs2;
    });
  } }, "to" in item ? /* @__PURE__ */ React41.createElement(Link, { to: item.to, onClick: () => action(item.to), style: {
    height
  } }, item.label) : /* @__PURE__ */ React41.createElement("button", { onClick: () => action(item.id), style: {
    height
  }, type: "button" }, item.label))))), renderIndicators && /* @__PURE__ */ React41.createElement(React41.Fragment, null, lineIndicator && clippedLineIndicator.visible && /* @__PURE__ */ React41.createElement(LineIndicator, { style: {
    left: clippedLineIndicator.left,
    width: clippedLineIndicator.width
  } }), tabIndicator && clippedTabIndicator.visible && /* @__PURE__ */ React41.createElement(TabIndicator, { style: {
    left: clippedTabIndicator.left,
    width: clippedTabIndicator.width,
    height,
    top: `calc(50% - ${padding.bottom / 2}px)`,
    ...variant === "soft" && {
      background: theme.colors.gray300
    },
    ...variant === "outline" && {
      border: `1px solid ${theme.colors.gray400}`
    }
  } })));
};

// src/linkTabs/index.tsx
var LinkTabs = ({
  current,
  links,
  tabIndicator,
  lineIndicator,
  height,
  gap,
  padding,
  variant,
  margin,
  maxWidth
}) => {
  let linkString = useMemo7(() => links.map((link) => link.to).join(","), [links]);
  let normalizedLinks = useMemo7(() => links.map((link) => ({
    ...link,
    to: new URL(link.to, window.location.origin).pathname
  })), [linkString]);
  let actualCurrent = useMemo7(() => {
    let normalizedCurrent = new URL(current, window.location.origin).pathname;
    let [bestMatch] = normalizedLinks.map((link) => link.to).filter((link) => link == normalizedCurrent || normalizedCurrent.startsWith(`${link}/`)).sort((a, b) => b.length - a.length);
    return bestMatch;
  }, [current, normalizedLinks]);
  return /* @__PURE__ */ React42.createElement(Tabs, { current: actualCurrent, tabs: normalizedLinks, action: () => {
  }, tabIndicator, lineIndicator, height, gap, margin, padding, variant, maxWidth });
};

// src/logo.tsx
import React43 from "react";
var Logo = ({
  size = 30,
  color = "#0099FF"
}) => {
  return /* @__PURE__ */ React43.createElement("svg", { height: size, width: size, viewBox: "0 0 56 56", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, /* @__PURE__ */ React43.createElement("path", { d: "M28.12 0L50 44.24L44 56L28.12 21L11.88 56L6 44.24L28.12 0Z", fill: color }));
};
var CenteredLogo = ({
  size
}) => {
  return /* @__PURE__ */ React43.createElement("div", { style: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  } }, /* @__PURE__ */ React43.createElement(Logo, { size }));
};

// src/menu/index.tsx
import * as RadixMenu from "@radix-ui/react-dropdown-menu";
import React44, { useEffect as useEffect11, useState as useState19 } from "react";
import { keyframes as keyframes10, styled as styled36 } from "styled-components";
var fadeInBottom3 = keyframes10`
  from { opacity: 0; transform: translateY(-10px) scale(0.99); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;
var fadeOutBottom3 = keyframes10`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-10px); }
`;
var fadeInTop2 = keyframes10`
  from { opacity: 0; transform: translateY(10px) scale(0.99); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;
var fadeOutTop2 = keyframes10`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(10px); }
`;
var fadeInLeft2 = keyframes10`
  from { opacity: 0; transform: translateX(-10px) scale(0.99); }
  to { opacity: 1; transform: translateX(0) scale(1); }
`;
var fadeOutLeft2 = keyframes10`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(-10px); }
`;
var fadeInRight3 = keyframes10`
  from { opacity: 0; transform: translateX(10px) scale(0.99); }
  to { opacity: 1; transform: translateX(0) scale(1); }
`;
var fadeOutRight3 = keyframes10`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(10px); }
`;
var Content17 = styled36(RadixMenu.Content).withConfig({
  componentId: "sc-12vdhr9-0"
})`display:flex;flex-direction:column;transition:all 0.3s ease;padding:5px;color:${theme.colors.background};box-shadow:${theme.shadows.large};background:${theme.colors.foreground};border-radius:10px;min-width:200px;gap:5px;&[data-state='open'][data-side='top']{animation:${fadeInTop2} 0.2s ease forwards;}&[data-state='closed'][data-side='top']{animation:${fadeOutTop2} 0.2s ease forwards;}&[data-state='open'][data-side='bottom']{animation:${fadeInBottom3} 0.2s ease forwards;}&[data-state='closed'][data-side='bottom']{animation:${fadeOutBottom3} 0.2s ease forwards;}&[data-state='open'][data-side='left']{animation:${fadeInLeft2} 0.2s ease forwards;}&[data-state='closed'][data-side='left']{animation:${fadeOutLeft2} 0.2s ease forwards;}&[data-state='open'][data-side='right']{animation:${fadeInRight3} 0.2s ease forwards;}&[data-state='closed'][data-side='right']{animation:${fadeOutRight3} 0.2s ease forwards;}`;
var Separator3 = styled36(RadixMenu.Separator).withConfig({
  componentId: "sc-12vdhr9-1"
})`height:1px;background:${theme.colors.gray800};`;
var Item8 = styled36(RadixMenu.Item).withConfig({
  componentId: "sc-12vdhr9-2"
})`font-size:14px;font-weight:500;display:flex;flex-direction:column;justify-content:center;text-align:left;gap:5px;padding:7px 13px;border-radius:6px;outline:none;border:none;background:transparent;transition:all 0.3s ease;color:${theme.colors.background};max-width:250px;&:not(:disabled){&:hover{background:${theme.colors.gray800};}&:focus{background:${theme.colors.gray800};}}&:disabled{opacity:0.7;}h1{font-size:14px;font-weight:500;color:${theme.colors.background};}p{font-size:12px;color:${theme.colors.gray600};}`;
var Title10 = styled36(RadixMenu.Label).withConfig({
  componentId: "sc-12vdhr9-3"
})`font-size:14px;font-weight:600;color:${theme.colors.gray100};padding:7px 13px;border-radius:6px;background:transparent;transition:all 0.3s ease;display:flex;align-items:center;gap:5px;`;
var Menu = ({
  children,
  label,
  onItemClick,
  items,
  title,
  setIsOpen
}) => {
  let [open, setOpen] = useState19(false);
  let zIndex = useDialogZIndex(open);
  useEffect11(() => setIsOpen?.(open), [open]);
  return /* @__PURE__ */ React44.createElement(RadixMenu.Root, { open, onOpenChange: setOpen }, /* @__PURE__ */ React44.createElement(RadixMenu.Trigger, { "aria-label": label, asChild: true }, children), /* @__PURE__ */ React44.createElement(RadixMenu.Portal, null, /* @__PURE__ */ React44.createElement(Content17, { sideOffset: 5, style: {
    zIndex
  } }, title && /* @__PURE__ */ React44.createElement(React44.Fragment, null, /* @__PURE__ */ React44.createElement(Title10, null, title), /* @__PURE__ */ React44.createElement(Separator3, null)), items.map((item, i) => item.type === "separator" ? /* @__PURE__ */ React44.createElement(Separator3, { key: i }) : /* @__PURE__ */ React44.createElement(Item8, { key: i, onClick: () => onItemClick?.(item.id), disabled: item.disabled, asChild: true }, /* @__PURE__ */ React44.createElement("button", { type: "button", disabled: item.disabled }, /* @__PURE__ */ React44.createElement("h1", null, item.label), item.description && /* @__PURE__ */ React44.createElement("p", null, item.description)))))));
};

// src/multiSelect/index.tsx
import * as RadixPopover2 from "@radix-ui/react-popover";
import { VisuallyHidden as VisuallyHidden5 } from "@radix-ui/react-visually-hidden";
import { RiArrowDownSLine as RiArrowDownSLine3 } from "@remixicon/react";
import React45, { useId as useId4 } from "react";
import { useMeasure as useMeasure2 } from "react-use";
import { keyframes as keyframes11, styled as styled37 } from "styled-components";
var fadeIn6 = keyframes11`
  from {
    transform: scale(0.95);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
`;
var Trigger10 = styled37(RadixPopover2.Trigger).attrs({
  type: "button"
}).withConfig({
  componentId: "sc-1nfjfai-0"
})`display:flex;outline:1px solid transparent;background:${theme.colors.gray300};color:${theme.colors.foreground};outline:none;width:100%;gap:10px;align-items:center;justify-content:space-between;transition:all 0.3s ease;border:none;font-weight:500;flex-shrink:0;&:focus-within,&:focus{background:${theme.colors.gray400};outline:1px solid ${theme.colors.gray600};}&[data-placeholder]{color:${theme.colors.gray700};}`;
var Content19 = styled37(RadixPopover2.Content).withConfig({
  componentId: "sc-1nfjfai-1"
})`display:flex;flex-direction:column;transition:all 0.3s ease;padding:15px;box-shadow:${theme.shadows.medium};background:${theme.colors.background};font-size:14px;font-weight:500;border-radius:8px;gap:10px;width:100%;&[data-state='open']{animation:${fadeIn6} 0.15s ease forwards;}`;
var Item9 = styled37("div").withConfig({
  componentId: "sc-1nfjfai-2"
})``;
var Value2 = styled37("div").withConfig({
  componentId: "sc-1nfjfai-3"
})`font-size:12px;color:${theme.colors.background};background:${theme.colors.foreground};padding:3px 5px;border-radius:5px;box-shadow:${theme.shadows.small};`;
var TriggerValues = styled37("div").withConfig({
  componentId: "sc-1nfjfai-4"
})`display:flex;gap:5px;flex-wrap:wrap;`;
var MultiSelect = ({
  size = "3",
  disabled,
  value,
  onChange,
  label,
  hideLabel,
  description,
  placeholder,
  error,
  items
}) => {
  let id = useId4();
  let sizeStyles = getButtonSize(size);
  let [ref, {
    width
  }] = useMeasure2();
  return /* @__PURE__ */ React45.createElement("div", { style: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0
  } }, label && (hideLabel ? /* @__PURE__ */ React45.createElement(VisuallyHidden5, null, /* @__PURE__ */ React45.createElement("label", { htmlFor: id }, label)) : /* @__PURE__ */ React45.createElement(InputLabel, { htmlFor: id }, label)), description && /* @__PURE__ */ React45.createElement(InputDescription, null, description), /* @__PURE__ */ React45.createElement(RadixPopover2.Root, null, /* @__PURE__ */ React45.createElement(Trigger10, { id, disabled, "aria-label": label, style: {
    ...sizeStyles,
    fontSize: 14
  }, ref }, /* @__PURE__ */ React45.createElement(TriggerValues, null, [...value ?? []].sort().map((v) => /* @__PURE__ */ React45.createElement(Value2, null, items.find((i) => i.id === v)?.label))), /* @__PURE__ */ React45.createElement(RiArrowDownSLine3, { size: 14, style: {
    opacity: 0.5
  } })), /* @__PURE__ */ React45.createElement(RadixPopover2.Portal, null, /* @__PURE__ */ React45.createElement(Content19, { sideOffset: 5, style: {
    width: width + 20,
    zIndex: 9999
  } }, items.map((item, i) => /* @__PURE__ */ React45.createElement(Item9, null, /* @__PURE__ */ React45.createElement(Checkbox, { checked: value?.includes(item.id), onCheckedChange: (checked) => {
    if (checked) {
      onChange?.([...value, item.id]);
    } else {
      onChange?.((value || [])?.filter((v) => v !== item.id));
    }
  }, label: item.label, disabled: item.disabled || disabled })))))), error && /* @__PURE__ */ React45.createElement(Error, null, error));
};

// src/optionToggle/index.tsx
import * as RadixToggleGroup from "@radix-ui/react-toggle-group";
import { VisuallyHidden as VisuallyHidden6 } from "@radix-ui/react-visually-hidden";
import React46, { useEffect as useEffect12, useId as useId5, useLayoutEffect as useLayoutEffect3, useMemo as useMemo8, useRef as useRef12, useState as useState20 } from "react";
import { styled as styled38 } from "styled-components";
var Wrapper16 = styled38("div").withConfig({
  componentId: "sc-1ign3j1-0"
})`display:flex;flex-direction:column;align-items:flex-start;gap:8px;width:100%;`;
var Root16 = styled38(RadixToggleGroup.Root).withConfig({
  componentId: "sc-1ign3j1-1"
})`position:relative;display:inline-flex;width:${({
  $fullWidth
}) => $fullWidth ? "100%" : "fit-content"};padding:4px;border-radius:${({
  $borderRadius
}) => $borderRadius};background:${theme.colors.gray300};isolation:isolate;transition:all 0.3s ease;&:focus-within{background:${theme.colors.gray300};}&[data-disabled]{opacity:0.55;pointer-events:none;}`;
var Indicator4 = styled38("div").withConfig({
  componentId: "sc-1ign3j1-2"
})`position:absolute;top:4px;left:4px;height:calc(100% - 8px);border-radius:${({
  $borderRadius
}) => $borderRadius};background:${theme.colors.background};box-shadow:${theme.shadows.small};transition:transform 0.24s cubic-bezier(0.22,1,0.36,1),width 0.24s cubic-bezier(0.22,1,0.36,1),opacity 0.2s ease;z-index:0;`;
var Item11 = styled38(RadixToggleGroup.Item).withConfig({
  componentId: "sc-1ign3j1-3"
})`position:relative;z-index:1;display:inline-flex;flex:${({
  $fullWidth
}) => $fullWidth ? "1 1 0" : "0 0 auto"};min-width:92px;min-height:${({
  $size
}) => getButtonSize($size).height};align-items:center;justify-content:center;border:none;border-radius:${({
  $borderRadius
}) => $borderRadius};background:transparent;padding:${({
  $size
}) => getButtonSize($size).padding};font-size:${({
  $size
}) => getButtonSize($size).fontSize};font-weight:500;color:${theme.colors.gray700};white-space:nowrap;cursor:pointer;transition:color 0.2s ease;&:hover,&:focus-visible{color:${theme.colors.gray900};outline:none;}&[data-state='on']{color:${theme.colors.gray900};}&:disabled{cursor:not-allowed;opacity:0.5;}`;
var OptionToggle = ({
  items,
  value,
  onChange,
  label,
  description,
  hideLabel,
  ariaLabel,
  disabled,
  fullWidth,
  size = "3"
}) => {
  let id = useId5();
  let labelId = `${id}-label`;
  let rootRef = useRef12(null);
  let itemRefs = useRef12({});
  let [indicator, setIndicator] = useState20(null);
  let sizeStyles = getButtonSize(size);
  let itemBorderRadius = `calc(${sizeStyles.borderRadius} - 2px)`;
  let selectedItem = useMemo8(() => items.find((item) => item.id == value), [items, value]);
  let updateIndicator = (nextIndicator) => {
    setIndicator((current) => {
      if (current === null && nextIndicator === null) return current;
      if (current && nextIndicator) {
        if (current.width === nextIndicator.width && current.left === nextIndicator.left) {
          return current;
        }
      }
      return nextIndicator;
    });
  };
  useLayoutEffect3(() => {
    let measure = () => {
      let root = rootRef.current;
      let item = selectedItem ? itemRefs.current[selectedItem.id] : null;
      if (!root || !item) {
        updateIndicator(null);
        return;
      }
      updateIndicator({
        width: item.offsetWidth,
        left: item.offsetLeft - 4
      });
    };
    measure();
    let resizeObserver = typeof ResizeObserver == "undefined" ? null : new ResizeObserver(() => {
      measure();
    });
    if (rootRef.current) resizeObserver?.observe(rootRef.current);
    if (selectedItem) {
      let selectedNode = itemRefs.current[selectedItem.id];
      if (selectedNode) resizeObserver?.observe(selectedNode);
    }
    return () => resizeObserver?.disconnect();
  }, [items, selectedItem, value]);
  useEffect12(() => {
    let handleResize = () => {
      let root = rootRef.current;
      let item = selectedItem ? itemRefs.current[selectedItem.id] : null;
      if (!root || !item) {
        updateIndicator(null);
        return;
      }
      updateIndicator({
        width: item.offsetWidth,
        left: item.offsetLeft - 4
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [selectedItem]);
  return /* @__PURE__ */ React46.createElement(Wrapper16, null, label ? hideLabel ? /* @__PURE__ */ React46.createElement(VisuallyHidden6, null, /* @__PURE__ */ React46.createElement(InputLabel, { id: labelId }, label)) : /* @__PURE__ */ React46.createElement(InputLabel, { id: labelId, style: {
    marginBottom: 0
  } }, label) : null, description && /* @__PURE__ */ React46.createElement(InputDescription, { style: {
    marginBottom: 0
  } }, description), /* @__PURE__ */ React46.createElement(Root16, { ref: rootRef, type: "single", value, onValueChange: (nextValue) => {
    if (!nextValue || nextValue == value) return;
    onChange?.(nextValue);
  }, disabled, "aria-label": !label ? ariaLabel : void 0, "aria-labelledby": label ? labelId : void 0, $fullWidth: fullWidth, $borderRadius: sizeStyles.borderRadius }, /* @__PURE__ */ React46.createElement(Indicator4, { "aria-hidden": true, $borderRadius: itemBorderRadius, style: {
    opacity: indicator ? 1 : 0,
    width: indicator?.width,
    transform: `translateX(${indicator?.left ?? 0}px)`
  } }), items.map((item) => /* @__PURE__ */ React46.createElement(Item11, { key: item.id, value: item.id, disabled: disabled || item.disabled, ref: (element) => {
    itemRefs.current[item.id] = element;
  }, $fullWidth: fullWidth, $size: size, $borderRadius: itemBorderRadius }, item.label))));
};

// src/or/index.tsx
import React47, { memo as memo6 } from "react";
import { styled as styled39 } from "styled-components";
var Wrapper17 = styled39("div").withConfig({
  componentId: "sc-1plcqt7-0"
})`display:grid;grid-template-columns:1fr min-content 1fr;gap:10px;width:100%;`;
var LineWrapper = styled39("div").withConfig({
  componentId: "sc-1plcqt7-1"
})`display:flex;align-items:center;gap:10px;`;
var Line = styled39("div").withConfig({
  componentId: "sc-1plcqt7-2"
})`height:1px;background-color:${theme.colors.gray400};flex-grow:1;`;
var Text2 = styled39("p").withConfig({
  componentId: "sc-1plcqt7-3"
})`color:${theme.colors.gray600};font-size:12px;font-weight:500;`;
var Or = memo6(({
  text
}) => {
  return /* @__PURE__ */ React47.createElement(Wrapper17, null, /* @__PURE__ */ React47.createElement(LineWrapper, null, /* @__PURE__ */ React47.createElement(Line, null)), /* @__PURE__ */ React47.createElement(Text2, null, text ?? "or"), /* @__PURE__ */ React47.createElement(LineWrapper, null, /* @__PURE__ */ React47.createElement(Line, null)));
});

// src/popover/index.tsx
import * as RadixPopover3 from "@radix-ui/react-popover";
import React48, { useEffect as useEffect13, useState as useState21 } from "react";
import { keyframes as keyframes12, styled as styled40 } from "styled-components";
var fadeInTop3 = keyframes12`
  from {
    opacity: 0;
    transform: translateY(-10px) scale(0.99);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;
var fadeOutTop3 = keyframes12`
  from {
    opacity: 1;
    transform: translateY(0) scale(0.99);
  }

  to {
    opacity: 0;
    transform: translateY(-10px) scale(1);
  }
`;
var fadeInBottom4 = keyframes12`
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.99);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;
var fadeOutBottom4 = keyframes12`
  from {
    opacity: 1;
    transform: translateY(0) scale(0.99);
  }

  to {
    opacity: 0;
    transform: translateY(10px) scale(1);
  }
`;
var fadeInLeft3 = keyframes12`
  from {
    opacity: 0;
    transform: translateX(-10px) scale(0.99);
  }

  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
`;
var fadeOutLeft3 = keyframes12`
  from {
    opacity: 1;
    transform: translateX(0) scale(0.99);
  }

  to {
    opacity: 0;
    transform: translateX(-10px) scale(1);
  }
`;
var fadeInRight4 = keyframes12`
  from {
    opacity: 0;
    transform: translateX(10px) scale(0.99);
  }

  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
`;
var fadeOutRight4 = keyframes12`
  from {
    opacity: 1;
    transform: translateX(0) scale(0.99);
  }

  to {
    opacity: 0;
    transform: translateX(10px) scale(1);
  }
`;
var Wrapper18 = styled40(RadixPopover3.Content).withConfig({
  componentId: "sc-1j5n8m1-0"
})`display:flex;gap:10;align-items:center;cursor:pointer;background:rgba(245,245,245,0.7);backdrop-filter:blur(10px);border-radius:10px;min-width:200px;will-change:transform,opacity;outline:none;border:solid 1px ${theme.colors.gray400};box-shadow:0 5px 15px rgba(0,0,0,0.1);&[data-state='open'][data-side='top']{animation:${fadeInBottom4} 0.2s ease forwards;}&[data-state='closed'][data-side='top']{animation:${fadeOutBottom4} 0.2s ease forwards;}&[data-state='open'][data-side='bottom']{animation:${fadeInTop3} 0.2s ease forwards;}&[data-state='closed'][data-side='bottom']{animation:${fadeOutTop3} 0.2s ease forwards;}&[data-state='open'][data-side='left']{animation:${fadeInLeft3} 0.2s ease forwards;}&[data-state='closed'][data-side='left']{animation:${fadeOutLeft3} 0.2s ease forwards;}&[data-state='open'][data-side='right']{animation:${fadeInRight4} 0.2s ease forwards;}&[data-state='closed'][data-side='right']{animation:${fadeOutRight4} 0.2s ease forwards;}`;
var ContentWrapper = styled40("div").withConfig({
  componentId: "sc-1j5n8m1-1"
})`padding:15px;`;
var Arrow4 = styled40(RadixPopover3.Arrow).withConfig({
  componentId: "sc-1j5n8m1-2"
})`fill:${theme.colors.background};`;
var Root18 = ({
  trigger,
  children,
  arrow,
  operationKey,
  onOpenChange,
  side,
  align,
  sideOffset,
  alignOffset,
  open: openProp
}) => {
  let [open, setOpen] = useState21(false);
  useEffect13(() => {
    setOpen(false);
  }, [operationKey]);
  useEffect13(() => {
    if (onOpenChange) onOpenChange(open);
  }, [open]);
  useEffect13(() => {
    if (typeof openProp === "boolean") setOpen(openProp);
  }, [openProp]);
  let zIndex = useDialogZIndex(open);
  return /* @__PURE__ */ React48.createElement(RadixPopover3.Root, { open, onOpenChange: setOpen }, /* @__PURE__ */ React48.createElement(RadixPopover3.Trigger, { asChild: true }, trigger), /* @__PURE__ */ React48.createElement(RadixPopover3.Portal, null, /* @__PURE__ */ React48.createElement(Wrapper18, { style: {
    zIndex
  }, side, align, sideOffset, alignOffset }, children, arrow && /* @__PURE__ */ React48.createElement(Arrow4, null))));
};
var Content21 = ({
  children,
  style
}) => {
  return /* @__PURE__ */ React48.createElement(ContentWrapper, { style }, children);
};
var Popover = Object.assign(Root18, {
  Content: Content21,
  Root: Root18
});

// src/select/index.tsx
import * as RadixSelect from "@radix-ui/react-select";
import { VisuallyHidden as VisuallyHidden7 } from "@radix-ui/react-visually-hidden";
import { RiArrowDownSLine as RiArrowDownSLine4, RiArrowUpSLine, RiCheckLine as RiCheckLine4 } from "@remixicon/react";
import React49, { useEffect as useEffect14, useId as useId6, useMemo as useMemo9, useState as useState22 } from "react";
import { keyframes as keyframes13, styled as styled41 } from "styled-components";
var fadeIn7 = keyframes13`
  from {
    transform: scale(0.95);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
`;
var Trigger13 = styled41(RadixSelect.Trigger).attrs({
  type: "button"
}).withConfig({
  componentId: "sc-d1sfay-0"
})`display:flex;outline:1px solid transparent;background:${theme.colors.gray300};color:${theme.colors.foreground};outline:none;width:100%;gap:10px;align-items:center;justify-content:space-between;transition:all 0.3s ease;border:none;font-weight:500;flex-shrink:0;&:focus{background:${theme.colors.gray300};outline:none;}&:focus-visible{background:${theme.colors.gray300};outline:1px solid ${theme.colors.gray600};}&[data-placeholder]{color:${theme.colors.gray700};}`;
var Content23 = styled41(RadixSelect.Content).withConfig({
  componentId: "sc-d1sfay-1"
})`display:flex;flex-direction:column;transition:all 0.3s ease;padding:5px;box-shadow:${theme.shadows.medium};background:${theme.colors.gray200};border:1px solid ${theme.colors.gray400};font-size:14px;font-weight:500;border-radius:10px;gap:5px;&[data-state='open']{animation:${fadeIn7} 0.15s ease forwards;}`;
var Separator5 = styled41(RadixSelect.Separator).withConfig({
  componentId: "sc-d1sfay-2"
})`height:1;background:${theme.colors.gray300};margin:5px 0;`;
var Item13 = styled41(RadixSelect.Item).withConfig({
  componentId: "sc-d1sfay-3"
})`font-size:14;font-weight:500;display:flex;align-items:center;gap:5px;padding:5px 10px 5px 5px;border-radius:6;outline:none;cursor:pointer;height:30px;border-radius:5px;&:hover{background:${theme.colors.gray300};}&:focus{background:${theme.colors.gray300};}&[data-state='checked']{background:${theme.colors.gray400};}&:disabled,&[data-disabled]{opacity:0.5;cursor:not-allowed;}`;
var Indicator5 = styled41(RadixSelect.ItemIndicator).withConfig({
  componentId: "sc-d1sfay-4"
})`display:flex;align-items:center;justify-content:center;width:20px;height:20px;opacity:0.5;`;
var Select = ({
  size = "3",
  disabled,
  value,
  onChange,
  label,
  hideLabel,
  description,
  placeholder,
  error,
  items
}) => {
  let id = useId6();
  let sizeStyles = getButtonSize(size);
  let normalizedValue = value === "" ? void 0 : value;
  let [isOpen, setIsOpen] = useState22(false);
  let zIndex = useDialogZIndex(isOpen);
  let disabledItems = useMemo9(() => items.filter((item) => item.disabled).map((item) => item.id).sort().join(","), [items]);
  useEffect14(() => {
    let disabledSelected = items.find((item) => item.id === normalizedValue && item.disabled);
    if (disabledSelected) {
      let notDisabled = items.find((item) => !item.disabled);
      if (notDisabled) onChange?.(notDisabled.id);
    }
  }, [normalizedValue, disabledItems, items, onChange]);
  return /* @__PURE__ */ React49.createElement("div", { style: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    textAlign: "initial"
  } }, label && (hideLabel ? /* @__PURE__ */ React49.createElement(VisuallyHidden7, null, /* @__PURE__ */ React49.createElement("label", { htmlFor: id }, label)) : /* @__PURE__ */ React49.createElement(InputLabel, { htmlFor: id }, label)), description && /* @__PURE__ */ React49.createElement(InputDescription, null, description), /* @__PURE__ */ React49.createElement(RadixSelect.Root, { value: normalizedValue, onValueChange: onChange, open: isOpen, onOpenChange: setIsOpen }, /* @__PURE__ */ React49.createElement(Trigger13, { id, disabled, "aria-label": label, style: {
    ...sizeStyles,
    fontSize: 14
  } }, /* @__PURE__ */ React49.createElement(RadixSelect.Value, { placeholder }), /* @__PURE__ */ React49.createElement(RadixSelect.Icon, null, /* @__PURE__ */ React49.createElement(RiArrowDownSLine4, { size: 14, style: {
    opacity: 0.5
  } }))), /* @__PURE__ */ React49.createElement(RadixSelect.Portal, null, /* @__PURE__ */ React49.createElement(Content23, { style: {
    zIndex
  } }, /* @__PURE__ */ React49.createElement(RadixSelect.ScrollUpButton, { className: "SelectScrollButton" }, /* @__PURE__ */ React49.createElement(RiArrowUpSLine, { size: 14 })), /* @__PURE__ */ React49.createElement(RadixSelect.Viewport, { className: "SelectViewport" }, items.map((item, i) => item.type == "separator" ? /* @__PURE__ */ React49.createElement(Separator5, { key: i }) : /* @__PURE__ */ React49.createElement(Item13, { value: item.id, key: i, disabled: item.disabled }, /* @__PURE__ */ React49.createElement("div", { style: {
    width: 20
  } }, /* @__PURE__ */ React49.createElement(Indicator5, null, /* @__PURE__ */ React49.createElement(RiCheckLine4, { size: 12, strokeWidth: 3.5 }))), /* @__PURE__ */ React49.createElement(RadixSelect.ItemText, null, item.label)))), /* @__PURE__ */ React49.createElement(RadixSelect.ScrollDownButton, { className: "SelectScrollButton" }, /* @__PURE__ */ React49.createElement(RiArrowDownSLine4, { size: 14 }))))), error && /* @__PURE__ */ React49.createElement(Error, null, error));
};

// src/showModal/confirm.tsx
import React50 from "react";
var confirm = ({
  title,
  description,
  onConfirm,
  onCancel,
  cancelText = "Cancel",
  confirmText = "Confirm"
}) => {
  showModal(({
    dialogProps
  }) => {
    return /* @__PURE__ */ React50.createElement(Alert.Wrapper, { ...dialogProps }, /* @__PURE__ */ React50.createElement(Alert.Title, null, title), /* @__PURE__ */ React50.createElement(Alert.Description, null, description), /* @__PURE__ */ React50.createElement(Alert.Actions, null, /* @__PURE__ */ React50.createElement(Alert.Action, { type: "cancel" }, /* @__PURE__ */ React50.createElement(Button, { type: "button", variant: "soft", onClick: onCancel }, cancelText)), /* @__PURE__ */ React50.createElement(Alert.Action, { type: "action" }, /* @__PURE__ */ React50.createElement(Button, { type: "button", variant: "solid", onClick: onConfirm }, confirmText))));
  });
};

// src/showModal/showModal.tsx
import React51, { useEffect as useEffect15, useRef as useRef13, useState as useState23 } from "react";
var modalAtom = atom([]);
var currentId = 0;
var showModal = (Body, opts = {}) => {
  let id = currentId++;
  let setOpenRef = {
    current: null
  };
  let hide = () => {
    setOpenRef = {
      current: null
    };
    setTimeout(() => {
      modalAtom.set((modals) => modals.filter((m) => m.id != id));
    }, 500);
  };
  let Component = () => {
    let [open, setOpen] = useState23(false);
    setOpenRef.current = setOpen;
    let onCloseInternal = () => {
      setOpen(false);
      hide();
      opts.onClose?.();
    };
    useEffect15(() => () => {
      setOpenRef.current = null;
    }, []);
    useEffect15(() => {
      setTimeout(() => setOpen(true), 2);
    }, []);
    let onOpenChange = (isOpen) => {
      setOpen(isOpen);
      if (!isOpen) onCloseInternal();
    };
    return /* @__PURE__ */ React51.createElement(Body, { isOpen: open, dialogProps: {
      isOpen: open,
      onOpenChange
    }, close: onCloseInternal });
  };
  modalAtom.set((modals) => [...modals, {
    id,
    component: Component
  }]);
  return {
    close: () => {
      if (setOpenRef.current) setOpenRef.current(false);
      hide();
    }
  };
};
var ModalRoot = () => {
  let modals = useAtom(modalAtom);
  let pathname = usePathname();
  useEffect15(() => {
    modalAtom.set([]);
  }, [pathname]);
  return /* @__PURE__ */ React51.createElement(React51.Fragment, null, modals.map((m) => /* @__PURE__ */ React51.createElement(m.component, { key: m.id })));
};
var usePathname = () => {
  let [pathname, setPathname] = useState23(() => typeof window != "undefined" ? window.location.pathname : "/");
  let currentPathRef = useRef13(pathname);
  currentPathRef.current = pathname;
  useEffect15(() => {
    let handler = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);
  useEffect15(() => {
    let to = setInterval(() => {
      if (currentPathRef.current != window.location.pathname) {
        setPathname(window.location.pathname);
      }
    }, 500);
    return () => clearInterval(to);
  }, []);
  return pathname;
};

// src/slider/index.tsx
import * as RadixSlider from "@radix-ui/react-slider";
import clsx2 from "clsx";
import React52, { useEffect as useEffect16, useRef as useRef14, useState as useState24 } from "react";
import { useDebounce } from "react-use";
import { styled as styled42 } from "styled-components";
var Wrapper19 = styled42.div.withConfig({
  componentId: "sc-44clea-0"
})`display:flex;flex-direction:column;.disabled{& > *{cursor:not-allowed !important;}}`;
var Header5 = styled42.header.withConfig({
  componentId: "sc-44clea-1"
})`display:flex;justify-content:space-between;align-items:center;`;
var Label2 = styled42.label.withConfig({
  componentId: "sc-44clea-2"
})`font-size:12px;font-weight:500;`;
var Input3 = styled42.input.withConfig({
  componentId: "sc-44clea-3"
})`border:solid 1px transparent;border-radius:5px;padding:4px 5px;font-size:12px;font-weight:500;outline:none;transition:all 0.2s ease;text-align:right;margin-right:-5px;color:${theme.colors.gray600};&:focus{border-color:${theme.colors.primary};color:${theme.colors.foreground};}&::-webkit-outer-spin-button,&::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;}&[type='number']{-moz-appearance:textfield;appearance:textfield;}`;
var Description9 = styled42.p.withConfig({
  componentId: "sc-44clea-4"
})`font-size:12px;margin-bottom:3px;color:${theme.colors.gray600};`;
var Root21 = styled42(RadixSlider.Root).withConfig({
  componentId: "sc-44clea-5"
})`position:relative;display:flex;align-items:center;user-select:none;touch-action:none;width:100%;height:20px;`;
var Track2 = styled42(RadixSlider.Track).withConfig({
  componentId: "sc-44clea-6"
})`background:#ddd;position:relative;flex-grow:1;border-radius:9999;height:3px;`;
var Range2 = styled42(RadixSlider.Range).withConfig({
  componentId: "sc-44clea-7"
})`position:absolute;background:${theme.colors.primary};border-radius:9999;height:100%;`;
var Thumb2 = styled42(RadixSlider.Thumb).withConfig({
  componentId: "sc-44clea-8"
})`display:block;width:20px;height:20px;background:white;border-radius:10px;border:solid 1px ${theme.colors.gray300};box-shadow:0 0 10px rgba(0,0,0,0.1);transition:all 0.2s ease;outline:none;&:focus{border:solid 1px ${theme.colors.primary};}`;
var Slider = ({
  value: value_,
  onChange: onChange_,
  description,
  step = 1,
  min = 0,
  max = 100,
  label,
  disabled,
  onValueCommit
}) => {
  let [value, setValue] = useState24(() => value_ ?? min ?? 0);
  let lastChangedRef = useRef14(0);
  let onChange = (value2) => {
    lastChangedRef.current = Date.now();
    setValue(value2);
  };
  useEffect16(() => {
    if (Date.now() - lastChangedRef.current < 50) return;
    onChange(value_ ?? min ?? 0);
  }, [value_]);
  useDebounce(() => {
    onChange_?.(value);
  }, 150, [value]);
  return /* @__PURE__ */ React52.createElement(Wrapper19, { className: clsx2({
    disabled
  }) }, /* @__PURE__ */ React52.createElement(Header5, null, /* @__PURE__ */ React52.createElement(Label2, null, label), /* @__PURE__ */ React52.createElement(Input3, { type: "number", value: value ?? 0, onChange: (e) => onChange?.(Math.max(min, Math.min(max, Number(e.target.value)))), min, max, step, disabled })), description && /* @__PURE__ */ React52.createElement(Description9, null, description), /* @__PURE__ */ React52.createElement(Root21, { step, min, max, value: typeof value == "number" ? [value] : void 0, onValueChange: ([value2]) => onChange?.(value2), onValueCommit: ([value2]) => onValueCommit?.(value2), "aria-label": label, disabled }, /* @__PURE__ */ React52.createElement(Track2, null, /* @__PURE__ */ React52.createElement(Range2, null)), /* @__PURE__ */ React52.createElement(Thumb2, null)));
};

// src/sortableCheckList/index.tsx
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { RiDraggable } from "@remixicon/react";
import React53, { useEffect as useEffect17, useState as useState25 } from "react";
import { styled as styled43 } from "styled-components";
var List4 = styled43("ul").withConfig({
  componentId: "sc-1ofe4dx-0"
})`list-style:none;padding:0;margin:0;display:flex;flex-direction:column;`;
var ListItem = styled43("li").withConfig({
  componentId: "sc-1ofe4dx-1"
})`padding:5px 0px;display:flex;gap:10px;align-items:center;width:100%;`;
var SortableCheckList = ({
  items,
  onChange,
  syncItems,
  sortable = true
}) => {
  let [innerItems, setInnerItems] = useState25(() => items);
  let sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));
  let setItems = (items2) => {
    setInnerItems(items2);
    onChange(items2);
  };
  useEffect17(() => {
    if (syncItems && items != innerItems) {
      setInnerItems(items);
    } else if (!syncItems) {
      let newIDs = new Set(items.map((i) => i.id));
      let currentIDs = new Set(innerItems.map((i) => i.id));
      if (newIDs.size !== currentIDs.size || [...newIDs].some((id) => !currentIDs.has(id))) {
        setInnerItems(items);
      }
    }
  }, [items, syncItems]);
  return /* @__PURE__ */ React53.createElement(DndContext, { sensors, collisionDetection: closestCenter, onDragEnd: (event) => {
    let {
      active,
      over
    } = event;
    if (active && over && active.id !== over.id) {
      let oldIndex = innerItems.findIndex((item) => item.id === active.id);
      let newIndex = innerItems.findIndex((item) => item.id === over.id);
      setItems(arrayMove(innerItems, oldIndex, newIndex));
    }
  } }, /* @__PURE__ */ React53.createElement(SortableContext, { items: innerItems, strategy: verticalListSortingStrategy }, /* @__PURE__ */ React53.createElement(List4, null, innerItems.map((item, i) => /* @__PURE__ */ React53.createElement(SortableItem, { key: `${item.id}-${i}`, id: item.id, item, sortable, onCheckedChange: () => {
    setItems(innerItems.map((i2) => {
      if (i2.id === item.id) {
        return {
          ...i2,
          isChecked: !i2.isChecked
        };
      }
      return i2;
    }));
  } })))));
};
var SortableItem = ({
  id,
  item,
  sortable,
  onCheckedChange
}) => {
  let {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({
    id
  });
  let style = {
    transform: CSS.Transform.toString(transform),
    transition
  };
  return /* @__PURE__ */ React53.createElement(ListItem, { "data-dnd-id": id, ref: setNodeRef, style }, sortable && /* @__PURE__ */ React53.createElement("div", { ...attributes, ...listeners, style: {
    display: "flex",
    alignItems: "center",
    cursor: "grab"
  } }, /* @__PURE__ */ React53.createElement(RiDraggable, { size: 16, color: String(theme.colors.gray600) })), /* @__PURE__ */ React53.createElement(Checkbox, { label: item.label, checked: item.isChecked, onCheckedChange }));
};
var CheckList = (p) => {
  return /* @__PURE__ */ React53.createElement(SortableCheckList, { ...p, sortable: false });
};

// src/switch/index.tsx
import * as RadixSwitch from "@radix-ui/react-switch";
import { VisuallyHidden as VisuallyHidden8 } from "@radix-ui/react-visually-hidden";
import clsx3 from "clsx";
import React54, { createRef, useId as useId7 } from "react";
import { styled as styled44 } from "styled-components";
var Wrapper20 = styled44("div").withConfig({
  componentId: "sc-xrz9xh-0"
})`display:flex;gap:10px;align-items:center;cursor:pointer;`;
var Root23 = styled44(RadixSwitch.Root).withConfig({
  componentId: "sc-xrz9xh-1"
})`display:flex;height:20px;width:30px;padding:0;justify-content:center;align-items:center;border-radius:50px;background:${theme.colors.gray300};border:solid 1px ${theme.colors.gray300};position:relative;transition:all 0.3s ease;&:focus{border:solid 1px ${theme.colors.blue900};}&[data-state='checked']{background:${theme.colors.blue900};}&.disabled{opacity:0.5;}`;
var Thumb4 = styled44(RadixSwitch.Thumb).withConfig({
  componentId: "sc-xrz9xh-2"
})`display:flex;height:14px;width:14px;border-radius:500;background:${theme.colors.background};box-shadow:none;position:absolute;transition:all 0.3s ease;border-radius:50px;top:2px;${Root23}[data-state='checked'] &{left:12px;}${Root23}[data-state='unchecked'] &{left:2px;}`;
var Switch = ({
  checked,
  onCheckedChange,
  label,
  disabled,
  description,
  hideLabel
}) => {
  let id = useId7();
  let root = createRef();
  return /* @__PURE__ */ React54.createElement(React54.Fragment, null, /* @__PURE__ */ React54.createElement(Wrapper20, null, /* @__PURE__ */ React54.createElement(Root23, { className: clsx3({
    disabled
  }), id, checked, onCheckedChange, ref: root }, /* @__PURE__ */ React54.createElement(Thumb4, null)), /* @__PURE__ */ React54.createElement("div", { onClick: (e) => root.current?.click(), style: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "4px"
  } }, hideLabel ? /* @__PURE__ */ React54.createElement(VisuallyHidden8, null, /* @__PURE__ */ React54.createElement("label", { htmlFor: id }, label)) : /* @__PURE__ */ React54.createElement(InputLabel, { htmlFor: id, style: {
    margin: 0
  }, onClick: (e) => e.stopPropagation() }, label), description && /* @__PURE__ */ React54.createElement(InputDescription, { style: {
    marginTop: 0,
    marginBottom: 0
  } }, description))));
};

// src/textArrayInput/index.tsx
import { RiDeleteBin4Line } from "@remixicon/react";
import React55, { useEffect as useEffect18, useRef as useRef15, useState as useState26 } from "react";
import { styled as styled45 } from "styled-components";
var Wrapper21 = styled45("div").withConfig({
  componentId: "sc-kg5jvs-0"
})`display:flex;flex-direction:column;gap:10px;`;
var Item14 = styled45("div").withConfig({
  componentId: "sc-kg5jvs-1"
})`display:flex;flex-direction:column;gap:5px;`;
var ItemInner2 = styled45("div").withConfig({
  componentId: "sc-kg5jvs-2"
})`display:flex;gap:10px;align-items:flex-end;`;
var TextArrayInput = ({
  value,
  description,
  label,
  error,
  onChange,
  placeholder,
  autoAdd,
  hideAddButton
}) => {
  useEffect18(() => {
    if (value.length === 0) onChange([""]);
  }, [value]);
  let [enterPressed, setEnterPressed] = useState26(false);
  let addedRef = useRef15(0);
  useEffect18(() => {
    if (Date.now() - addedRef.current < 100) return;
    addedRef.current = Date.now();
    if (autoAdd && value[value.length - 1] !== "") onChange([...value, ""]);
  }, [value, autoAdd]);
  return /* @__PURE__ */ React55.createElement(React55.Fragment, null, label && /* @__PURE__ */ React55.createElement(InputLabel, null, label), description && /* @__PURE__ */ React55.createElement(InputDescription, null, description), label && /* @__PURE__ */ React55.createElement(Spacer, { height: 5 }), /* @__PURE__ */ React55.createElement(Wrapper21, null, value.map((v, i) => /* @__PURE__ */ React55.createElement(Item14, { key: i }, /* @__PURE__ */ React55.createElement(ItemInner2, null, /* @__PURE__ */ React55.createElement(Input, { value: v, placeholder, onChange: (e) => {
    let newValue = [...value];
    newValue[i] = e.target.value;
    onChange(newValue);
  }, onKeyDown: (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setEnterPressed(true);
      onChange([...value, ""]);
    }
  }, label, style: {
    flexGrow: 1
  }, size: "2", hideLabel: true, autoFocus: i == value.length - 1 && enterPressed }), /* @__PURE__ */ React55.createElement(Button, { onClick: () => onChange(value.filter((_, j) => j != i)), title: "Remove", iconLeft: /* @__PURE__ */ React55.createElement(RiDeleteBin4Line, null), size: "2", variant: "soft", disabled: value.length === 1, type: "button" })), Array.isArray(error) && error[i] && /* @__PURE__ */ React55.createElement(Error, null, error[i]))), !autoAdd && !hideAddButton && /* @__PURE__ */ React55.createElement("div", { style: {
    display: "flex",
    gap: 10
  } }, /* @__PURE__ */ React55.createElement(Button, { size: "2", onClick: () => onChange([...value, ""]), type: "button" }, "Add"))));
};

// src/toaster/index.tsx
import React56 from "react";
import { Toaster as BaseToaster, toast as toast3 } from "sonner";
var Toaster = () => /* @__PURE__ */ React56.createElement(BaseToaster, { position: "bottom-right", richColors: true });
export {
  Accordion,
  AccordionSingle,
  Alert,
  AnimateContent,
  AnimateHeight,
  AnimatePanes,
  Attributes,
  Avatar,
  Badge,
  Button,
  Callout,
  CardLinks,
  CenteredLogo,
  CenteredSpinner,
  CheckList,
  Checkbox,
  Code,
  Combobox,
  ContextMenu,
  Control,
  Copy,
  Datalist,
  DatePicker,
  Dialog,
  Entity,
  Error,
  Flex,
  Group2 as Group,
  InfoTooltip,
  InlineCopy,
  Input,
  InputDescription,
  InputLabel,
  Kbd,
  LargePanelDialog,
  LinkButton,
  LinkTabs,
  Logo,
  Menu,
  ModalRoot,
  MultiSelect,
  OptionToggle,
  Or,
  Panel,
  Popover,
  RenderDate,
  Select,
  Slider,
  SortableCheckList,
  Spacer,
  Spinner,
  Switch,
  Tabs,
  Text,
  TextArrayInput,
  Title3 as Title,
  Toaster,
  Tooltip,
  atom,
  buttonTheme,
  confirm,
  getButtonSize,
  getButtonStyles,
  showModal,
  theme,
  toast3 as toast,
  useAtom,
  useCopy,
  useDialog,
  useDialogContext,
  useIsGroup,
  useIsInDialog
};
//# sourceMappingURL=index.js.map